// Copyright 2021-2026 - Hewlett Packard Enterprise Company
/* eslint-disable react-refresh/only-export-components */
import axios from 'axios';
import {
  isSelected,
  getIdFromUri,
  getAllPages,
  parseIloGeneration,
} from '../utils/apiHelpers';
import { convertVolumeSizeGBToGiB } from '../utils/inputHelpers';
import {
  FIRMWARE_UPDATE_TEMPLATE_URI,
  SERVER_FIRMWARE_DOWNLOAD_TEMPLATE_URI,
  GROUP_FIRMWARE_DOWNLOAD_TEMPLATE_URI,
  OV_FIRMWARE_UPDATE_TEMPLATE_URI,
  FIRMWARE_BATCH_UPDATE_TEMPLATE_URI,
  APPLIANCE_BATCH_UPDATE_TEMPLATE_URI,
  SCHEDULE_JOB_URL,
  GEN_10,
  GEN_11,
  GEN_12,
  GEN_13,
  SERVERS_CATEGORY_FILTER,
  SERVER_INVENTORY_TEMPLATE_URI,
  UPDATE_ILO_FIRMWARE_SERVER_TEMPLATE_URI,
  JOB_STATE,
  SETTING_CATEGORY_FIRMWARE,
  SERVER_ILO_FW_UPDATE,
  SERVER_INVENTORY_TEMPLATE_JSON,
  NETWORK_CONNECTIVITY_TEMPLATE_URI,
  OV_SERVER_TEMPLATE_UPDATE_URI,
  EXTERNAL_STORAGE_COMPLIANCE_TEMPLATE_URI,
  SERVER_VOLUME_DETAILS_TEMPALTE_URI,
  REFRESH_APPLIANCE_SETTINGS_TEMPLATE_URI,
  APPLY_OV_APPLIANCE_SETTINGS_TEMPLATE_URI,
  DIRECT_CONNECT,
  COPY_OV_SERVER_TEMPLATE_URI,
  OV_APPLIANCE_TYPE,
  SETTING_CATEGORY_STORAGE,
  TARGET_DEVICE_SECURE_GATEWAY,
  TARGET_DEVICE_ILO,
  SECURE_GATEWAY,
  SERVER_FW_UPDATE,
  ALL_SERVERS_FILTER_MGMT_URL,
  SERVER_RECONNECT_TEMPLATE_URI,
  UTILIZATION_METRIC_TYPE_MAP,
  CONNECTION_TYPE_ENUM,
  SERVER_LOG_TEMPLATE_URI,
  DOWNLOADED_BASELINE_OPTION,
  DISCOVER_SERVERS_TEMPLATE_URI,
  MAX_ITEMS,
  CUSTOM_BASELINE_TYPE,
  GATEWAY,
  ONE_VIEW,
  TRUE,
  FALSE,
  AUTODISCOVER_METHOD,
  MANUAL_ILO_METHOD,
  DISCOVER_SERVERS_TEMPLATE_ID,
  ASYNC_OPERATIONS_INITIAL_NEXT,
  FIRMWARE_UPDATE_TEMPLATE_ID,
  RESET_ILO_BMC_TEMPLATE_URI,
  SERVER_RESOURCE_TYPE,
  SUPPORT_CASE_POLL_TIMEOUT,
  SELECT_SUBSCRIPTION_KEY_AUTO_DISCOVER,
  SUPPORT_AHS_REFRESH_TOPIC,
} from '../constants';
import { URLS, COUNTS } from './api/api-constants';
import { PRODUCT_CAPABILITIES } from '../constants/productCapabilities';

const SERVICE_NOW_NAME = 'ServiceNow';
export const SERVICE_NOW_TYPE = 'SERVICE_NOW';
const ARUBA_CENTRAL_NAME = 'Aruba Central';
export const ARUBA_CENTRAL_TYPE = 'ARUBA_CENTRAL';
const DSCC_NAME = 'Data Services';
export const DSCC_TYPE = 'DSCC';
const OAUTH = 'OAUTH';
export const VMWARE_VCENTER_TYPE = 'VMWARE_VCENTER';

function getPaginatedIlos(start, count, sort) {
  const params = {};

  start && (params.start = start);
  count && (params.count = count);
  sort && (params.sort = sort);

  return axios.get(URLS.ILOS_URL, {
    params,
  });
}

export async function getILOs() {
  const includePaginationMetaData = true;
  return getAllPages(
    (offset, limit) =>
      getPaginatedIlos(
        offset,
        limit,
        null, // sort
      ),
    COUNTS.MAX_COBALT_COUNT,
    includePaginationMetaData,
  );
}

export function getParamsForServers(
  offset,
  count,
  sort,
  searchString,
  filterQueries,
  tagQueries,
) {
  const params = {};
  const filters = filterQueries || [];
  const tags = tagQueries || [];

  offset && (params.offset = offset);
  count && (params.limit = count);
  sort && (params.sort = sort);
  searchString &&
    (params.search = `${searchString
      .trim()
      .replaceAll('\\', '\\\\')
      .replaceAll("'", "\\'")}`);

  if (filters && filters.length > 0) {
    if (filters instanceof Array) {
      params.filter = `(${filters.join(') and (')})`;
    } else {
      params.filter = filters;
    }
  }

  if (tags && tags.length > 0) {
    if (tags instanceof Array) {
      params['filter-tags'] = `(${tags.join(') or (')})`;
    } else {
      params['filter-tags'] = tags;
    }
  }
  return params;
}

export function getAllServersV1(
  sort,
  searchString = null,
  filterQueries = null,
  tagQueries = null,
) {
  return axios.get(URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL, {
    params: getParamsForServers(
      undefined,
      undefined,
      sort,
      searchString,
      filterQueries,
      tagQueries,
    ),
  });
}

// !!! PLEASE READ !!!
// Before you add new queries to this or other functions
// STRONGLY consider including whatever you are querying
// in the existing filterQueries parameter. Any new filterable
// fields added to the UI need to be included in cleanslate
// (saved filters), or else the list of servers returned
// to the UI will not match the list of servers returned
// by a user's saved filter, which is bad. For more
// information on how to add a new field to cleanslate, see
// https://github.com/compute-cloud/cleanslate/blob/main/docs/maintenance.md
export function getServers(
  offset,
  count,
  sort,
  searchString,
  filterQueries,
  tagQueries,
  signal,
) {
  const params = getParamsForServers(
    offset,
    count,
    sort,
    searchString,
    filterQueries,
    tagQueries,
  );

  if (PRODUCT_CAPABILITIES.requiresIncludeAllDataOnServers) {
    params['include-all-data'] = true;
  }

  return axios.get(URLS.SERVERS_URL, {
    params,
    signal,
  });
}

export function getServersTotalCount() {
  return axios.get(URLS.SERVERS_URL, {
    params: { limit: 1, offset: 0 },
  });
}

export function getServersOverview(
  offset,
  count,
  sort,
  searchString,
  filterQueries,
  tagQueries,
) {
  const params = getParamsForServers(
    offset,
    count,
    sort,
    searchString,
    filterQueries,
    tagQueries,
  );
  return axios.get(URLS.SERVERS_OVERVIEW_URL, {
    params,
  });
}

export async function getAllServersOverviewByFilter(
  filterQueries = null,
  searchString = null,
  tagQueries = null,
) {
  return getAllPages(
    (offset, limit) =>
      getServersOverview(
        offset,
        limit,
        null, // sort
        searchString ? searchString.trim() : null,
        filterQueries,
        tagQueries,
      ),
    COUNTS.MAX_DOORWAY_COUNT,
  );
}

export function getGroupIloSettingsCompliances(id) {
  return new Promise(resolve => {
    if (id) {
      axios
        .get(`${URLS.OPS_MGMT_GROUPS_V1B2_URL}/${id}/ilo-settings-compliance`)
        .then(res => {
          if (res?.status === 200) {
            resolve(res?.data);
          } else {
            resolve({});
          }
        })
        .catch(error => resolve({}));
    } else {
      resolve({});
    }
  });
}

export function getServersData(
  format,
  sort,
  searchString,
  filterQueries,
  tagQueries,
) {
  const params = {};
  const filters = filterQueries || [];
  const tags = tagQueries || [];

  format && (params.format = format);
  sort && (params.sort = sort);
  searchString &&
    (params.search = `${searchString
      .trim()
      .replaceAll('\\', '\\\\')
      .replaceAll("'", "\\'")}`);

  if (filters && filters.length > 0) {
    if (filters instanceof Array) {
      params.filter = `(${filters.join(') and (')})`;
    } else {
      params.filter = filters;
    }
  }

  if (tags && tags.length > 0) {
    if (tags instanceof Array) {
      params['filter-tags'] = `(${tags.join(') or (')})`;
    } else {
      params['filter-tags'] = tags;
    }
  }

  return axios.get(URLS.SERVERS_DATA_URL, {
    params,
  });
}

// !!! See comment above getServers before adding new queries !!!
export async function getAllSelectedServers(
  selected,
  unselected,
  allSelected,
  searchString,
  filterQueries,
  tagQueries,
) {
  // If selected servers are 0 < 10 disregard any other filter and filter only
  // selected servers.
  if (
    allSelected !== true &&
    selected &&
    selected.length > 0 &&
    selected.length < COUNTS.MAX_FILTER_GET_ALL_SERVERS
  ) {
    const selectedQueries = [];
    selected.forEach(id => {
      selectedQueries.push(`id eq '${id}'`);
    });
    const queriesToAdd = `(${selectedQueries.join(') or (')})`;
    // Check length of query to make sure filter=<length> is not greater than 2000.
    // getServers() wraps 'and(<filterQueries>)', adding 5 characters: 2000-5 = 1995
    // In the event our query is too long we wont modify filterQueries and
    // default to grabbing all servers
    queriesToAdd.length <= COUNTS.MAX_FILTER_LENGTH_GET_ALL_SERVERS &&
      filterQueries &&
      filterQueries.splice(0, filterQueries.length, queriesToAdd);
  }

  const results = await getAllPages(
    (offset, limit) =>
      getServers(
        offset,
        limit,
        null, // sort
        searchString ? searchString.trim() : null,
        filterQueries,
        tagQueries,
      ),
    COUNTS.MAX_DOORWAY_COUNT,
  );

  const servers = [];

  results.forEach(server => {
    if (isSelected(server.id, selected, unselected, allSelected)) {
      servers.push(server);
    }
  });

  return servers;
}

export async function getAllServersByFilter(
  signal,
  filterQueries = null,
  searchString = null,
  tagQueries = null,
) {
  return getAllPages(
    (offset, limit) =>
      getServers(
        offset,
        limit,
        null, // sort
        searchString ? searchString.trim() : null,
        filterQueries,
        tagQueries,
        signal,
      ),
    COUNTS.MAX_DOORWAY_COUNT,
  );
}

export function getServerCounts(fields) {
  return axios.get(URLS.SERVERS_COUNTS_URL, { params: { fields } });
}

export function getServer(id, tenantAcid) {
  return axios.get(`${URLS.SERVERS_URL}/${id}`, {
    forceTenantAcid: tenantAcid,
  });
}

export function getServerInventory(id) {
  return axios.get(
    `${URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL}/${id}/inventory`,
  );
}

export function getServerSetting(id, tenantAcid) {
  return axios
    .get(`${URLS.SERVER_SETTINGS_URL}/${id}`, {
      forceTenantAcid: tenantAcid,
    })
    .then(response => {
      if (
        response?.data &&
        response.status >= 200 &&
        response.status <= 299 &&
        response.data.category === SETTING_CATEGORY_STORAGE &&
        response.data.settings?.DEFAULT?.raidType
      ) {
        const tempStorageObject = {
          id: null,
          raidType: response.data.settings.DEFAULT.raidType,
          capacityInGiB:
            response.data.settings.DEFAULT.volumeSizeInGB === -1
              ? null
              : convertVolumeSizeGBToGiB(
                  response.data.settings.DEFAULT.volumeSizeInGB,
                ),
          driveCount: null,
          spareDriveCount: null,
          driveTechnology: null,
          ioPerfModeEnabled: null,
          readCachePolicy: null,
          writeCachePolicy: null,
        };

        response.data.settings.DEFAULT.volumes = [];
        response.data.settings.DEFAULT.volumes.push(tempStorageObject);
      }
      return response;
    });
}

export function fetchSpecServerSetting(
  serverSettings,
  specServerSettingName,
  tenantAcid,
) {
  serverSettings = serverSettings.filter(
    setting =>
      setting.id && setting?.category?.toUpperCase() === specServerSettingName,
  );
  const settingRequests = serverSettings.map(serverSetting =>
    getServerSetting(serverSetting.id, tenantAcid).then(settingResp => {
      Object.assign(serverSetting, settingResp.data);
    }),
  );
  return settingRequests;
}

// Get a page of groups
export async function getGroups(
  start,
  count,
  sort,
  searchString,
  filterQueries,
  tagQueries,
  fetchServerSettings = false,
) {
  const params = {};
  const filters = filterQueries || [];
  const tags = tagQueries || [];
  start && (params.offset = start);
  count && (params.limit = count);
  sort && (params.sort = sort);
  searchString &&
    (params.search = `${searchString
      .trim()
      .replaceAll('\\', '\\\\')
      .replaceAll("'", "\\'")}`);

  if (filters && filters.length > 0) {
    params.filter = `(${filters.join(') and (')})`;
  }

  if (tags && tags.length > 0) {
    params.tags = `${tags.join(';')}`;
  }
  const groupsResp = await axios.get(URLS.GROUPS_URL_V2, {
    params,
  });

  if (fetchServerSettings) {
    const groupList = groupsResp.data.items;
    const firmware = SETTING_CATEGORY_FIRMWARE.toUpperCase();
    const settingRequests = [];
    for (let index = 0; index < groupList.length; index++) {
      const serverSettings = groupList[index].serverSettings_ || [];
      settingRequests.push(fetchSpecServerSetting(serverSettings, firmware));
    }

    await Promise.all(
      settingRequests.map(async settingRequest =>
        Promise.all(settingRequest).then(_ => groupsResp),
      ),
    );
  }
  return groupsResp;
}

export async function getAllSelectedGroups(
  selected,
  unselected,
  allSelected,
  searchString,
  filterQueries,
  tagQueries,
) {
  const groups = [];

  // If selected groups are 0 < 10 disregard any other filter and filter only
  // selected groups.
  if (
    allSelected !== true &&
    selected &&
    selected.length > 0 &&
    selected.length < COUNTS.MAX_FILTER_GET_ALL_SERVERS
  ) {
    const selectedQueries = [];
    selected.forEach(id => {
      selectedQueries.push(`id eq '${id}'`);
    });
    const queriesToAdd = `(${selectedQueries.join(') or (')})`;
    // Check length of query to make sure filter=<length> is not greater than 2000.
    // getgroups() wraps 'and(<filterQueries>)', adding 5 characters: 2000-5 = 1995
    // In the event our query is too long we wont modify filterQueries and
    // default to grabbing all groups
    queriesToAdd.length <= COUNTS.MAX_FILTER_LENGTH_GET_ALL_SERVERS &&
      filterQueries &&
      filterQueries.splice(0, filterQueries.length, queriesToAdd);
  }

  const results = await getAllPages(
    (offset, limit) =>
      getGroups(
        offset,
        limit,
        null, // sort
        searchString ? searchString.trim() : null,
        filterQueries,
        tagQueries,
      ),
    COUNTS.MAX_DOORWAY_COUNT,
  );

  results.forEach(group => {
    if (isSelected(group.id, selected, unselected, allSelected)) {
      groups.push(group);
    }
  });

  return groups;
}

export async function getAllGroups(
  sort,
  filterQueries,
  fetchServerSettings = false,
) {
  return getAllPages(
    (offset, limit) =>
      getGroups(
        offset,
        limit,
        sort,
        null, // search
        filterQueries,
        null, // tags
        fetchServerSettings,
      ),
    COUNTS.MAX_DOORWAY_COUNT,
  );
}
// get servers with hardware security risk for a group
export async function getHardwareSecurityRiskServers(groupId) {
  const url = `${URLS.OPS_MGMT_SECURE_HARDWARE_GROUPS_URL}/${groupId}/secure-devices`;
  return axios.get(url, {
    params: {
      filter: "serverSecureHardwareStatus eq 'RISK'",
    },
  });
}

// get baseline data for a device with hardware security risk
export async function getHardwareSecurityRiskBaseline(groupId, deviceId) {
  // eslint-disable-next-line max-len
  const url = `${URLS.OPS_MGMT_SECURE_HARDWARE_GROUPS_URL}/${groupId}/devices/${deviceId}/baseline`;
  return axios.get(url);
}

// get delta data for a device with hardware security risk
export async function getHardwareSecurityRiskDelta(
  groupId,
  deviceId,
  inventory,
) {
  // eslint-disable-next-line max-len
  const url = `${URLS.OPS_MGMT_SECURE_HARDWARE_GROUPS_URL}/${groupId}/devices/${deviceId}/delta`;
  return axios.get(url, {
    params: {
      select: `${inventory}`,
    },
  });
}

// update hardware baseline for a group
export async function updateHardwareBaseline(groupId, serverIds) {
  const url = `${URLS.OPS_MGMT_SECURE_HARDWARE_GROUPS_URL}/${groupId}/baseline`;
  return axios.patch(url, {
    items: serverIds.map(id => ({ id })),
    data: { serverSecureHardwareStatus: 'OK' },
  });
}

// Get a group by id
export async function getGroup(id, tenantAcid, fetchServerSettings = false) {
  const groupResp = await axios.get(`${URLS.GROUPS_URL_V2}/${id}`, {
    forceTenantAcid: tenantAcid,
  });

  if (fetchServerSettings) {
    // also fetch any firmware server settings, so that we have firmwareBundleName
    const serverSettings = groupResp.data.serverSettings_ || [];
    const firmware = SETTING_CATEGORY_FIRMWARE.toUpperCase();
    const settingRequests = fetchSpecServerSetting(
      serverSettings,
      firmware,
      tenantAcid,
    );
    return Promise.all(settingRequests).then(_ => groupResp);
  }
  return groupResp;
}

export async function getAllILOComplianceServersInGroup(groupId, filter) {
  const response = await fetch(
    `/api/groups/${groupId}/ilo-compliance-servers?filter=${JSON.stringify(filter)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch ILO compliance servers');
  }

  const data = await response.json();
  return data;
}

export function deleteGroup(id) {
  const url = URLS.OPS_MGMT_GROUP_POST_PATCH_DEL_V1_URL;

  return axios.delete(`${url}/${id}`, {
    params: { force: true },
  });
}

export function addDeviceToGroup(groupId, payload) {
  // default url
  const url = URLS.OPS_MGMT_GROUP_POST_PATCH_DEL_V1_URL;

  return axios.post(`${url}/${groupId}/devices`, payload);
}

export function removeDevicesFromGroup(groupId, deviceIds, resetSubsystems) {
  const params = {};
  if (resetSubsystems) {
    if (Array.isArray(resetSubsystems) && resetSubsystems.length > 0) {
      params['reset-subsystems'] = resetSubsystems.join(',');
    } else if (typeof resetSubsystems === 'string' && resetSubsystems !== '') {
      params['reset-subsystems'] = resetSubsystems;
    }
  }

  const url = URLS.OPS_MGMT_GROUP_POST_PATCH_DEL_V1_URL;
  return axios.post(
    `${url}/${groupId}/devices/unassign`,
    {
      devices: deviceIds.map(id => ({ deviceId: id })),
    },
    { params: { ...params } },
  );
}

export async function getAllServersInGroup(groupId, sort, filterQueries = []) {
  const localFilterQueries = [`group/id eq '${groupId}'`, ...filterQueries];

  return getAllPages(
    (offset, limit) =>
      getServers(
        offset,
        limit,
        sort,
        null, // search
        localFilterQueries,
      ),
    COUNTS.MAX_DOORWAY_COUNT,
  );
}

export async function performPreFlightChecksV2(
  serverIdWithSppId,
  swDriversSelected,
  downgradeSelected,
  powerOffNeeded,
  testConnection,
  isDownloadFirmwareAction = false,
) {
  const payload = {
    mode: 'withJob',
    serverSppIds: serverIdWithSppId,
    installSoftwareAndDrivers: swDriversSelected,
    downgradeFirmwareBundle: downgradeSelected,
    powerOff: powerOffNeeded,
  };

  // Handle custom baseline case - add baseline_type only for custom baselines
  const hasCustomBaselines = serverIdWithSppId.some(
    item => item.isCustomBaseline,
  );

  if (hasCustomBaselines) {
    payload.serverSppIds = serverIdWithSppId.map(item => {
      const enhancedItem = { serverId: item.serverId, sppId: item.sppId };
      // If this is a custom baseline (settingId instead of regular sppId)
      if (item.isCustomBaseline) {
        enhancedItem.baseline_type = CUSTOM_BASELINE_TYPE;
      }
      return enhancedItem;
    });
  }

  payload.performConnectivityTest = testConnection;

  if (isDownloadFirmwareAction) {
    payload.bundleUpdateMethod = 'Stage';
  } else {
    payload.bundleUpdateMethod = 'Update';
  }
  return axios.post(URLS.FW_PREFLIGHT_URL, payload);
}

export async function performOsInstallPreflightChecks(serverId) {
  const url = `${URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL}/${serverId}/analyze-os-install`;
  return axios.post(url, {});
}

// Get a page of groups using a server setting
export function getGroupsUsingSetting(settingId, offset, limit) {
  const url = URLS.OPS_MGMT_SETTING_V1_URI_PREFIX;
  const settingUri = `${url}/${settingId}`;
  const params = {};
  params.filter = `'${settingUri}' in settingsUris`;
  offset && (params.offset = offset);
  limit && (params.limit = limit);

  const groupUrl = URLS.OPS_MGMT_GROUP_POST_PATCH_DEL_V1_URL;
  return axios.get(`${groupUrl}`, {
    params,
  });
}

export async function getAllGroupsUsingSetting(settingId) {
  return getAllPages(
    (offset, limit) => getGroupsUsingSetting(settingId, offset, limit),
    COUNTS.MAX_DOORWAY_COUNT,
  );
}

export function createGroup(payload) {
  const url = URLS.OPS_MGMT_GROUP_POST_PATCH_DEL_V1_URL;

  return axios.post(url, payload, { skipTenantForbidden: true });
}

export function updateGroup(id, payload) {
  const url = URLS.OPS_MGMT_GROUP_POST_PATCH_DEL_V1_URL;

  return axios.patch(`${url}/${id}`, payload, {
    headers: { 'content-type': 'application/merge-patch+json' },
  });
}

export function getGroupCounts() {
  return axios.get(`${URLS.GROUP_COUNTS_URL}`);
}

export function getGroupActivities(groupId) {
  const NUM_ITEMS_IN_GROUP_ACTIVITY_CARD = 10;
  const params = {};
  params.groups = NUM_ITEMS_IN_GROUP_ACTIVITY_CARD;
  params.filter = `(groupId eq '${groupId}')`;

  return axios.get(URLS.UI_DOORWAY_ACTIVITIES_URL, {
    params,
  });
}

export function getServerSettingsActivities(id) {
  const NUM_ITEMS_IN_SETTING_ACTIVITY_CARD = 10;
  const params = {};
  params.groups = NUM_ITEMS_IN_SETTING_ACTIVITY_CARD;
  params.filter = `(serverSettingsId eq '${id}')`;

  return axios.get(URLS.UI_DOORWAY_ACTIVITIES_URL, {
    params,
  });
}

export function getPolicyActivities(id) {
  const NUM_ITEMS_IN_POLICIES_ACTIVITY_CARD = 10;
  const params = {};
  params.groups = NUM_ITEMS_IN_POLICIES_ACTIVITY_CARD;
  params.filter = `(source/id eq '${id}')`;

  return axios.get(URLS.UI_DOORWAY_ACTIVITIES_URL, {
    params,
  });
}

// Get all recent activities based on type
export function getActivities(type) {
  const params = {};
  params.groups = COUNTS.NUM_ITEMS_IN_ACTIVITY_CARD;
  params.filter = `(source/type eq '${type}')`;

  return axios.get(URLS.UI_DOORWAY_ACTIVITIES_URL, {
    params,
  });
}

export function getActivityTotalCount() {
  const params = {
    offset: 0,
    limit: 1,
  };

  return axios.get(URLS.UI_DOORWAY_ACTIVITIES_URL, {
    params,
  });
}

// Get a page of tags
export function getTags(next, limit, sort, filterQueries) {
  const params = { next: null };
  const filters = filterQueries || [];

  next && (params.next = next);
  limit && (params.limit = limit);
  sort && (params.sort = sort);

  if (filters && filters.length > 0) {
    params.filter = `(${filters.join(') and (')})`;
  }
  return axios.get(URLS.TAGS_URL, { params });
}

export async function getAllTags(sort, filterQueries) {
  const tags = [];
  let first = true;
  let next = null;
  let fetch = null;
  let limit = COUNTS.MAX_DOORWAY_COUNT;

  while (first || next) {
    // do not attempt to fetch beyond opensearch's max limit
    if (tags.length >= MAX_ITEMS) break;
    if (tags.length + limit > MAX_ITEMS) {
      limit = MAX_ITEMS - tags.length;
    }

    // Must wait for each response to get the
    // next parameter for the next request.
    // eslint-disable-next-line no-await-in-loop
    fetch = await getTags(next, limit, sort, filterQueries).catch(e => {
      throw e;
    });
    next = fetch?.data?.next ? fetch.data.next : null;
    if (fetch?.data?.items) {
      fetch.data.items.forEach(tag => {
        tags.push(tag);
      });
    }
    first = false;
  }
  return tags;
}

export function getTagNames(
  filterTags = null,
  resourceUri = null,
  filter = null,
) {
  const params = {};
  if (filterTags) {
    params['filter-tags'] = filterTags;
  } else if (resourceUri) {
    params['resource-uri'] = resourceUri;
  }
  if (filter) params.filter = filter;
  return axios.get(URLS.TAG_NAMES_URL, { params });
}

// Get a page of server settings through v2 api
export function getServerSettings(offset, limit, sort, filterQueries) {
  const params = {};
  const filters = filterQueries || [];
  offset && (params.offset = offset);
  limit && (params.limit = limit);
  sort && (params.sort = sort);

  if (filters && filters.length > 0) {
    if (filters instanceof Array) {
      params.filter = `(${filters.join(') and (')})`;
    } else {
      params.filter = filters;
    }
  }

  return axios.get(URLS.SERVER_SETTINGS_URL, {
    params,
  });
}

// Get all server settings through v2 api
export async function getAllServerSettings(sort, filterQueries) {
  return getAllPages(
    (offset, limit) => getServerSettings(offset, limit, sort, filterQueries),
    COUNTS.MAX_DOORWAY_COUNT,
  );
}

export function createServerSetting(payload) {
  const url = URLS.OPS_MGMT_SETTINGS_V1_POST_PATCH_DEL_URL;
  return axios.post(url, payload);
}

export function updateServerSetting(id, payload) {
  const url = URLS.OPS_MGMT_SETTINGS_V1_POST_PATCH_DEL_URL;
  return axios.patch(`${url}/${id}`, payload, {
    headers: { 'content-type': 'application/merge-patch+json' },
  });
}

export function deleteServerSetting(id) {
  const url = URLS.OPS_MGMT_SETTINGS_V1_POST_PATCH_DEL_URL;
  return axios.delete(`${url}/${id}`);
}

export function updateServerAutoIloFw(deviceIds, payload, batchPatchFlag) {
  if (batchPatchFlag) {
    return axios.patch(
      `${URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL_BULK}`,
      payload,
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
  }
  // add deviceids to query string in the format baseurl?id=x&id=y...
  // replace the '+' in id with url encoding %2B
  let idQueryStr = '';
  deviceIds.map(id => (idQueryStr += `id=${id.replace(/\+/g, '%2B')}&`));
  return axios.patch(
    `${URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL}?${idQueryStr}`,
    payload,
    {
      headers: { 'content-type': 'application/merge-patch+json' },
    },
  );
}

export function updateServerAutoSupportCase(payload) {
  return axios.patch(
    `${URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL_BULK}`,
    payload,
    {
      headers: { 'Content-Type': 'application/json' },
    },
  );
}

// Get all recent activities
export function getRecentActivities() {
  return axios.get(URLS.UI_DOORWAY_ACTIVITIES_URL, {
    params: {
      groups: COUNTS.NUM_ITEMS_IN_ACTIVITY_CARD,
    },
  });
}

function getStartOfDay(timestamp) {
  const startOfDay = new Date(timestamp);
  startOfDay.setHours(0, 0, 0, 0);
  return startOfDay.toISOString();
}

function getEndOfDay(timestamp) {
  const endOfDay = new Date(timestamp);
  endOfDay.setHours(0, 0, 0, 0);
  endOfDay.setDate(endOfDay.getDate() + 1);
  return endOfDay.toISOString();
}

// Get a page of activities
export function getPaginatedActivities(
  start,
  count,
  sort,
  searchString,
  dateFilter,
  filterQueries,
  usePlaintextSearch = false,
) {
  const params = {
    offset: start,
    limit: count,
    sort,
  };
  const filters = [];

  if (usePlaintextSearch) {
    // Use search parameter instead of filter to enable full-text search
    // across all activity fields (backend handles multi-field search)
    if (searchString && searchString.trim()) {
      params.search = searchString.trim();
    }
  } else {
    // Legacy filter-based search on source/displayName field only
    searchString &&
      filters.push(
        `contains(toupper(source/displayName),'${searchString
          .trim()
          .replaceAll("'", "''")
          .toUpperCase()}')`,
      );
  }

  if (dateFilter) {
    if (dateFilter[0]) {
      filters.push(`(createdAt ge ${getStartOfDay(dateFilter[0])})`);
    }
    if (dateFilter[1]) {
      filters.push(`(createdAt lt ${getEndOfDay(dateFilter[1])})`);
    }
  }

  // filterQueries are the checkboxes in the activity page filter layer
  // all of the checkbox's queries get OR'ed together and are not exclusive
  filterQueries &&
    filterQueries.length > 0 &&
    filters.push(`(${filterQueries.join(') or (')})`);

  // search + date + the result of the check boxes OR'ed together all get
  // AND'ed together so results must match the name and date, but only one of
  // the check boxes
  if (filters.length > 0) {
    params.filter = `(${filters.join(') and (')})`;
  }

  return axios.get(URLS.UI_DOORWAY_ACTIVITIES_URL, {
    params,
  });
}

export function createJob(payload) {
  return axios.post(URLS.JOBS_URL, payload);
}

export function createJobV1B2(payload) {
  return axios.post(URLS.COMPUTE_OPS_MGMT_JOBS_URL, payload);
}

export function createJobV1(payload) {
  return axios.post(URLS.COMPUTE_OPS_MGMT_JOBS_URL_V1, payload);
}

export function resetIloBmc(resourceUri) {
  const serverId = getIdFromUri(resourceUri);
  const payload = {
    jobTemplateUri: RESET_ILO_BMC_TEMPLATE_URI,
    resourceUri: `${URLS.OPS_MGMT_SERVERS_V1_URI_PREFIX}/${serverId}`,
  };
  return createJob(payload);
}

export function createSchedule(payload) {
  return axios.post(URLS.COMPUTE_OPS_MGMT_SCHEDULE_URL, payload);
}

export function getSchedules(start, count, sort, filterQueries) {
  const params = {};
  const filters = filterQueries || [];
  start && (params.start = start);
  count && (params.count = count);
  sort && (params.sort = sort);

  if (filters && filters.length > 0) {
    if (filters instanceof Array) {
      params.filter = `(${filters.join(') and (')})`;
    } else {
      params.filter = filters;
    }
  }
  return axios.get(URLS.COMPUTE_OPS_MGMT_SCHEDULE_URL, {
    params,
  });
}

export async function getAllResources(resourceMethod, sort, filterQueries) {
  return getAllPages(
    (offset, limit) => resourceMethod(offset, limit, sort, filterQueries),
    COUNTS.MAX_DOORWAY_COUNT,
  );
}

export function getAllSchedules(sort, filterQueries) {
  return getAllResources(getSchedules, sort, filterQueries);
}

export function getIloFWUpdateSchedules() {
  return getAllSchedules(
    null,
    `purpose eq '${SERVER_ILO_FW_UPDATE}' and nextStartAt ne null`,
  );
}

export function deleteSchedule(id) {
  return axios.delete(`${URLS.COMPUTE_OPS_MGMT_SCHEDULE_URL}/${id}`);
}

export function deleteSchedules(schedules) {
  const proms = [];
  schedules.forEach(schedule => {
    proms.push(deleteSchedule(schedule.id));
  });
  return Promise.all(proms);
}

export function updateSchedule(id, payload) {
  return axios.patch(`${URLS.COMPUTE_OPS_MGMT_SCHEDULE_URL}/${id}`, payload, {
    headers: { 'content-type': 'application/merge-patch+json' },
  });
}

export function getBundles(offset, count, url) {
  const params = {};
  offset && (params.offset = offset);
  count && (params.limit = count);
  return axios.get(url, { params });
}

export async function getFirmwareBundles(uidoorwayApiFlag) {
  const includePaginationMetaData = true;
  const results = await getAllPages(
    (offset, limit) =>
      getBundles(
        offset,
        limit,
        uidoorwayApiFlag
          ? URLS.FIRMWARE_BUNDLES_URL
          : URLS.FIRMWARE_BUNDLES_URL_V1,
      ),
    COUNTS.MAX_DOORWAY_COUNT,
    includePaginationMetaData,
  );

  if (results?.data?.items) {
    results.data.items.forEach(item => {
      item.selfUri = item.resourceUri?.replace('/compute-ops-mgmt', '');
      item.resourceType = item.type;
      item.type = item.bundleType ? item.bundleType.toLowerCase() : '';
      item.platformFamily = item.platformFamily
        ? item.platformFamily.toLowerCase()
        : '';
      item.hotfixBaseUri = item.baseBundleUri
        ? item.baseBundleUri.replace('/compute-ops-mgmt', '')
        : null;
    });
  }

  return results;
}

export async function getFirmwareBundleById(bundleId) {
  return axios.get(`${URLS.FIRMWARE_BUNDLES_URL_V1}/${bundleId}`);
}

export async function getFirmwareBundleDetails(bundleId) {
  return axios.get(
    `${URLS.FIRMWARE_BUNDLES_URL_V1}/${bundleId}/bundle-details`,
  );
}

export async function createCustomFirmwareBundle(payload) {
  return axios.post(`${URLS.FIRMWARE_BUNDLES_URL_V1}/custom`, payload);
}

export function getApplianceFirmwareBundles() {
  const params = {};
  params.start = 0;
  params.limit = COUNTS.MAX_APPLIANCE_FW_BUNDLE_COUNT;
  return axios.get(URLS.APPLIANCE_FW_BUNDLE_UIDOORWAY_URL, { params });
}

export function getApplicableApplianceFirmwareBundles(applianceType) {
  const params = {};
  params.start = 0;
  params.limit = COUNTS.MAX_APPLIANCE_FW_BUNDLE_COUNT;
  params.filter = `(isActive eq true) and (applianceType eq '${applianceType.toUpperCase()}')`;

  return axios.get(URLS.APPLIANCE_FW_BUNDLE_UIDOORWAY_URL, { params });
}

export function getApplianceFirmware(applianceFirmwareId) {
  return axios.get(
    `${URLS.APPLIANCE_FW_BUNDLE_UIDOORWAY_URL}/${applianceFirmwareId}`,
  );
}

export function getFWULatest() {
  const params = {};
  params.start = 0;
  return axios.get(`${URLS.FIRMWARE_UPGRADES_URL}/latest`, params);
}

export function getFirmwareCaReportsOverview(sppId) {
  return axios.get(`${URLS.FIRMWARE_CA_REPORTS_URL}/overview`, {
    params: { filter: sppId },
  });
}

export function getFirmwareCaReportServerDetail(serverId) {
  return axios.get(`${URLS.FIRMWARE_CA_REPORTS_URL}/servers/${serverId}`);
}

export function getFirmwareCaReportComponentDetail(componentId) {
  return axios.get(`${URLS.FIRMWARE_CA_REPORTS_URL}/components/${componentId}`);
}

export function getFirmwareAnalysisServersToUpdate({
  filter,
  search,
  filterTags,
  sort,
  limit = 10,
  offset = 0,
}) {
  return axios.get(`${URLS.FIRMWARE_ANALYSIS_URL}/servers-to-update`, {
    params: {
      filter,
      search: search || undefined,
      'filter-tags': filterTags || undefined,
      sort: sort || undefined,
      limit,
      offset,
    },
  });
}

export function getFirmwareAnalysisComponentsToUpdate({
  filter,
  search,
  filterTags,
  sort,
  limit = 10,
  offset = 0,
}) {
  return axios.get(`${URLS.FIRMWARE_ANALYSIS_URL}/components-to-update`, {
    params: {
      filter,
      search: search || undefined,
      'filter-tags': filterTags || undefined,
      sort: sort || undefined,
      limit,
      offset,
    },
  });
}

export function getFirmwareAnalysisServersUpToDate({
  filter,
  nameSearch,
  sort,
  limit = 10,
  offset = 0,
}) {
  return axios.get(`${URLS.FIRMWARE_ANALYSIS_URL}/servers-up-to-date`, {
    params: {
      filter: filter || undefined,
      nameSearch: nameSearch || undefined,
      sort: sort || undefined,
      limit,
      offset,
    },
  });
}

export function getFirmwareAnalysisAdvisoryDetail(advisoryId) {
  return axios.get(`${URLS.FIRMWARE_CA_REPORTS_URL}/advisories/${advisoryId}`);
}

export function getFirmwareAnalysisAdvisoriesResolved({
  filter,
  search,
  filterTags,
  sort,
  limit = 10,
  offset = 0,
}) {
  return axios.get(`${URLS.FIRMWARE_ANALYSIS_URL}/customer-advisories`, {
    params: {
      filter,
      search: search || undefined,
      'filter-tags': filterTags || undefined,
      sort: sort || undefined,
      limit,
      offset,
    },
  });
}

export function getFirmwareAnalysisExport({
  type,
  filter,
  search,
  filterTags,
  sort,
  format,
}) {
  return axios.get(`${URLS.FIRMWARE_ANALYSIS_URL}/export`, {
    params: {
      type,
      filter,
      search: search || undefined,
      'filter-tags': filterTags || undefined,
      sort: sort || undefined,
      format,
    },
    responseType: 'blob',
  });
}

export function getFWUUpdate() {
  const params = {};
  params.start = 0;
  return axios.get(`${URLS.FIRMWARE_UPGRADES_URL}/update`, params);
}

/*
 * No uidoorway version of this method is available because
 * displayAppliances=true filter is not supported by uidoorway API
 * This will return list in applicableAppliances which is calculated by backend
 * Phoenix changes may be required to support returning applicableAppliances in the response
 */
export function getApplianceFWULatest(applianceType, stableApplianceFwFlag) {
  const params = {};
  params.start = 0;

  params.filter = stableApplianceFwFlag
    ? `isActive eq true and applianceType eq '${applianceType.toUpperCase()}'`
    : `and(eq(isActive,true),eq(applianceType, '${applianceType.toUpperCase()}'))`;

  const applianceFwBundleUrl = stableApplianceFwFlag
    ? URLS.APPLIANCE_FIRMWARE_BUNDLES_URL_V1
    : URLS.APPLIANCE_FIRMWARE_BUNDLES_URL;

  return axios.get(
    `${applianceFwBundleUrl}?sort=applianceVersion:desc&limit=1&displayAppliances=true`,
    { params },
  );
}

export function getBiosSettings() {
  return axios.get(URLS.BIOS_SETTINGS_URL);
}
export function cancelPendingJobs(parentjobId) {
  const payload = { input: { stopOnRequest: 'true' } };
  return axios.patch(`${URLS.JOBS_URL}/${parentjobId}`, payload);
}

export function cancelWaitingFWUJob(jobId) {
  const payload = { input: { cancel: true } };
  return axios.patch(`${URLS.JOBS_URL}/${jobId}`, payload);
}

export function cancelAnalyzeJob(parentjobId) {
  const payload = { input: { skipPreflightCheck: 'true' } };
  return axios.patch(`${URLS.JOBS_URL}/${parentjobId}`, payload);
}

export function dismissStalledJob(jobId) {
  const dismissPayload = { state: 'Error' };
  return axios.patch(`${URLS.JOBS_URL}/${jobId}`, dismissPayload);
}

export function getServerActivities(serverId) {
  const NUM_ITEMS_IN_SERVER_ACTIVITY_CARD = 10;
  const params = {};
  params.offset = 0;
  params.limit = NUM_ITEMS_IN_SERVER_ACTIVITY_CARD;
  // eslint-disable-next-line max-len
  params.filter = `(associatedServerUri eq '${URLS.ACTIVITIES_ASSOCIATED_SERVER_PREFIX}/${serverId}')`;

  return axios.get(URLS.UI_DOORWAY_ACTIVITIES_URL, {
    params,
  });
}

export function getSupportCaseId(caseRequestId) {
  return axios.get(URLS.SUPPORT_CASES_URL, {
    params: { filter: `eq(case_request_id,'${caseRequestId}')` },
    timeout: SUPPORT_CASE_POLL_TIMEOUT,
  });
}

export function getLastNotMonitoredServerActivity(serverId) {
  const params = {};
  params.offset = 0;
  params.limit = 1;
  // eslint-disable-next-line max-len
  params.filter = `(associatedServerUri eq '${URLS.ACTIVITIES_ASSOCIATED_SERVER_PREFIX}/${serverId}') and ((key in ('SERVER_NOT_MONITORED')))`;

  return axios.get(URLS.UI_DOORWAY_ACTIVITIES_URL, {
    params,
  });
}

export function getApplianceActivities(appId, applianceType) {
  const NUM_ITEMS_IN_SERVER_ACTIVITY_CARD = 10;
  const params = {};
  params.offset = 0;
  params.limit = NUM_ITEMS_IN_SERVER_ACTIVITY_CARD;
  // assoiciateServerUri having servers reference in it.
  // Once fixed at lime, this should be updated correctly.
  if (applianceType === OV_APPLIANCE_TYPE.GATEWAY) {
    // eslint-disable-next-line max-len
    params.filter = `(associatedServerUri eq '${URLS.ACTIVITIES_ASSOCIATED_SERVER_PREFIX}/gateway+${appId}')`;
  } else {
    // eslint-disable-next-line max-len
    params.filter = `(associatedServerUri eq '${URLS.ACTIVITIES_ASSOCIATED_SERVER_PREFIX}/oneview+${appId}')`;
  }

  return axios.get(URLS.UI_DOORWAY_ACTIVITIES_URL, {
    params,
  });
}

export function getGatewayOnboardDiscoverServersActivity(appId) {
  const params = {};
  params.offset = 0;
  params.limit = 1;
  // assoiciateServerUri having servers reference in it.
  // Once fixed at lime, this should be updated correctly.
  // eslint-disable-next-line max-len
  params.filter = `(associatedServerUri eq '${URLS.ACTIVITIES_ASSOCIATED_SERVER_PREFIX}/gateway+${appId}' and (key in ('ONBOARD_DISCOVER_SERVER_COMPLETED', 'ONBOARD_DISCOVER_SERVER_FAILED')))`;
  params.sort = 'createdAt desc';

  return axios.get(URLS.UI_DOORWAY_ACTIVITIES_URL, {
    params,
  });
}

export function getServerGroupAlerts(
  groupId,
  offset = 0,
  limit = COUNTS.MAX_DOORWAY_COUNT,
) {
  const params = { offset, limit };
  params.filter =
    "not category eq 'security' and not category eq 'UtilizationThreshold' and clearedAt eq null";
  params.sort = 'severity desc,updatedAt desc';
  return axios
    .get(`${URLS.GROUPS_URL_V2}/${groupId}/alerts`, { params })
    .then(r => {
      // group alerts by serverId
      const obj =
        r.data.items?.reduce((obj, item) => {
          const serverName = item.server_?.name || item.serverId || 'unknown';
          if (!obj[serverName]) {
            obj[serverName] = [];
          }
          obj[serverName].push(item);
          return obj;
        }, {}) || {};
      r.data.items = Object.entries(obj).map(([device, alerts]) => ({
        device,
        alerts,
      }));
      return r;
    });
}

export function getAllServerGroupAlerts(groupId) {
  return getAllPages(
    (offset, limit) => getServerGroupAlerts(groupId, offset, limit),
    COUNTS.MAX_DOORWAY_COUNT,
  ).then(entries => {
    // merge entries for the same server from different pages together
    const obj = entries.reduce((obj, { device, alerts }) => {
      if (!obj[device]) {
        obj[device] = [];
      }
      obj[device].push(...alerts);
      return obj;
    }, {});
    const mergedEntries = Object.entries(obj);
    // getAllPages returns an array - re-wrap for code expecting axios response
    return {
      data: {
        offset: 0,
        count: mergedEntries.length,
        total: mergedEntries.length,
        items: mergedEntries.map(([device, alerts]) => ({
          device,
          alerts,
        })),
      },
    };
  });
}

export function getServerInventoryData(
  { id, format },
  connectionType,
  serversFlag,
) {
  const url = `${URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL}/${id}/inventory?`;

  const hwInventoryTemplateJson = SERVER_INVENTORY_TEMPLATE_JSON;
  if (connectionType === CONNECTION_TYPE_ENUM.CREDENTIAL) {
    // For credential connection, device data will be availabel
    // in pciDevice which is a redfish standard.
    hwInventoryTemplateJson.pciDevice = {
      data: [
        {
          Status: null,
          Location: null,
          Name: null,
          ProductVersion: null,
          FirmwareVersion: null,
          SerialNumber: null,
          PartNumber: null,
        },
      ],
    };
  }

  if (format === 'csv') {
    return axios.post(url.concat('format=csv'), hwInventoryTemplateJson, {
      headers: { 'content-type': 'application/json' },
    });
  }
  return axios.post(url.concat('format=json'), hwInventoryTemplateJson, {
    headers: { 'content-type': 'application/json' },
  });
}

export function collectServerLogData(id, collectAhsLog) {
  const jobTemplateUri = SERVER_LOG_TEMPLATE_URI;
  const requestBody = {
    resourceUri: `/api/compute/v1/servers/${id}`,
    jobTemplateUri: `${jobTemplateUri}`,
    data: {
      collect_ahs_log: collectAhsLog,
    },
  };
  const promise = createJob({
    ...requestBody,
  });
  return promise;
}

export function collectDiscoverServerDataId(secureGatewayId, jobStableApiFlag) {
  let promise;

  if (jobStableApiFlag) {
    promise = createJobV1({
      jobTemplate: DISCOVER_SERVERS_TEMPLATE_ID,
      jobParams: {
        discoveryMode: AUTODISCOVER_METHOD,
        secureGatewayId,
      },
    });
  } else {
    promise = createJob({
      jobTemplateUri: DISCOVER_SERVERS_TEMPLATE_URI,
      resourceUri: `${URLS.GATEWAY_APPLIANCE_URL}+${secureGatewayId}`,
      data: {
        discoveryMode: AUTODISCOVER_METHOD,
      },
    });
  }
  return promise;
}

export function verifyManualEntryServers(
  ipAddressList,
  secureGatewayId,
  jobStableApiFlag,
) {
  let promise;

  if (jobStableApiFlag) {
    promise = createJobV1({
      jobTemplate: DISCOVER_SERVERS_TEMPLATE_ID,
      jobParams: {
        discoveryMode: MANUAL_ILO_METHOD,
        ipAddressList,
        secureGatewayId,
      },
    });
  } else {
    promise = createJob({
      jobTemplateUri: DISCOVER_SERVERS_TEMPLATE_URI,
      resourceUri: `${URLS.GATEWAY_APPLIANCE_URL}+${secureGatewayId}`,
      data: {
        discoveryMode: MANUAL_ILO_METHOD,
        ips: ipAddressList,
      },
    });
  }
  return promise;
}

export function getDiscoveredServers(
  hideServers,
  externalManager,
  onboarding,
  gatewayId,
  method,
  offset,
  limit,
) {
  const params = {};
  if (hideServers !== undefined && method === AUTODISCOVER_METHOD)
    params.hide = hideServers;
  params.externalManager = externalManager;
  if (method === AUTODISCOVER_METHOD) params.onboarding = onboarding;
  if (gatewayId) params.applianceID = gatewayId;
  if (method) params.method = method;
  limit && (params.limit = limit);
  params.offset = offset;
  return axios.get(`${URLS.OPS_MGMT_DISCOVERED_SERVERS_URL}`, { params });
}

export function getDiscoveredManualEntryServers(gatewayId, method) {
  const params = {};
  if (gatewayId) params.applianceID = gatewayId;
  if (method) params.method = method;
  return axios.get(`${URLS.OPS_MGMT_DISCOVERED_SERVERS_URL}`, { params });
}

export function downloadServerLog(id) {
  return axios.get(
    `${URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL}/${id}/download-logs`,
  );
}

export function getLastCollectionData(gatewayId, method) {
  const params = {
    applianceID: gatewayId,
    method,
  };

  return axios.get(`${URLS.OPS_MGMT_DISCOVERED_SERVERS_LAST_COLLECTION_URL}`, {
    params,
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function getDeviceDnsNtpData(gatewayId) {
  const URL = URLS.OPS_MGMT_GATEWAY_DNS_NTP_DATA_URL.replace(
    '{device_id}',
    `${GATEWAY}+${gatewayId}`,
  );
  return axios.get(URL, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function downloadFromS3(preSignedUrl) {
  return axios.get(preSignedUrl, {
    omitAuth: true,
    responseType: 'blob',
  });
}

export function getServerNetworkConnectivity(serverId) {
  return axios.get(`${URLS.SERVERS_URL_V1}/${serverId}/tor-port-mapping`);
}

export function createSavedFilter(payload) {
  return axios.post(URLS.OPS_MGMT_SAVED_FILTERS_URL, payload);
}

export async function listSavedFilters(resourceType) {
  const pageSize = 10000; // max page size allowed by cleanslate
  const params = {
    sort: 'name asc',
    limit: `${pageSize}`,
    filter: `filterResourceType eq '${resourceType}'`,
  };

  const saveFiltersUrl = URLS.OPS_MGMT_SAVED_FILTERS_URL;

  const firstResp = await axios.get(saveFiltersUrl, { params });

  // The UI does not currently paginate the list of saved filters,
  // so fetch all of them if there are multiple pages.
  if (
    firstResp?.data?.total &&
    firstResp?.data?.count &&
    firstResp.data.total > firstResp.data.count
  ) {
    const numPages = Math.floor(firstResp.data.total / pageSize);
    const offsets = Array.from(
      Array(numPages),
      (_, page) => (page + 1) * pageSize,
    );

    // ideally this should be using getAllPages to benefit from a max results limit,
    // but the logic here is too customized to easily fit that pattern
    const requests = offsets.map(index =>
      axios.get(saveFiltersUrl, { params: { offset: index, ...params } }),
    );
    const otherResps = await Promise.all(requests);
    const allItems = [firstResp.data?.items]
      .concat(otherResps.map(resp => resp?.data?.items))
      .flat();

    // If we are unlucky, changes to filters during fetches could result in dups/missing items
    // Duplicates would be most obvious, so at least eliminate those:
    const seen = {};
    const uniq = allItems.filter(item => {
      if (!item || !item?.id || item.id in seen) {
        return false;
      }
      seen[item.id] = true;
      return true;
    });
    return {
      status: 200,
      data: { count: uniq.length, total: uniq.length, items: uniq },
    };
  }

  return firstResp;
}

export function deleteSavedFilter(id) {
  return axios.delete(`${URLS.OPS_MGMT_SAVED_FILTERS_URL}/${id}`);
}

export function patchSavedFilter(id, payload) {
  return axios.patch(`${URLS.OPS_MGMT_SAVED_FILTERS_URL}/${id}`, payload, {
    headers: { 'content-type': 'application/merge-patch+json' },
  });
}

export const createGroupBaselineUpdatRequest = async ({
  group,
  selectedDevices,
  serial,
  stopOnFailure,
  installSWDrivers,
  startTime,
  downgrade,
  powerOff,
  waitForReboot,
  rebootTimeout,
  isAnyDevicePoweredOn,
  cohesityFlag,
  baseline,
  installswdriversOverrides,
}) => {
  const successes = [];
  const errors = [];
  const authErrors = [];
  let apiCall = createJob;
  let requestBody = {
    resourceUri: group?.resourceUri,
    jobTemplateUri: FIRMWARE_BATCH_UPDATE_TEMPLATE_URI,
    data: {
      devices: selectedDevices.map(dev => dev.id),
      parallel: !serial,
      stopOnFailure,
      installSWDrivers,
    },
  };
  if (downgrade != null) {
    requestBody.data.downgrade = downgrade;
  }

  requestBody.data.powerOff = powerOff;
  requestBody.data.is_preflightcheck_skipped = false;

  // Handle custom baseline case
  if (baseline?.componentExclusionsEnabled && baseline?.settingId) {
    requestBody.data.bundle_id = baseline.settingId;
    requestBody.data.baseline_type = CUSTOM_BASELINE_TYPE;
  }

  if (cohesityFlag) {
    requestBody.data.wait_for_power_off_or_reboot = waitForReboot;
    requestBody.data.wait_for_power_off_or_reboot_timeout = rebootTimeout.value;
  }

  requestBody.data.skip_blocklisted_components = true;
  if (installswdriversOverrides?.length > 0) {
    requestBody.data.installswdriversOverrides = installswdriversOverrides;
  }

  if (startTime) {
    const groupBaselineUpdateRequestBody = { ...requestBody };

    requestBody = {
      name: `${group?.name}`,
      purpose: 'GROUP_FW_UPDATE',
      description: JSON.stringify({
        is_any_device_powered_on: isAnyDevicePoweredOn,
      }),
      associatedResourceUri: group?.resourceUri,
      operation: {
        type: 'REST',
        method: 'POST',
        uri: SCHEDULE_JOB_URL,
        body: groupBaselineUpdateRequestBody,
      },
      schedule: {
        startAt: startTime,
      },
    };
    apiCall = createSchedule;
  }

  let response;
  try {
    response = await apiCall({
      ...requestBody,
    });
    if (response && response.status >= 200 && response.status <= 299) {
      successes.push(...selectedDevices);
    }
  } catch (e) {
    if (e && e.response && e.response.status === 403) {
      authErrors.push(...selectedDevices);
    } else {
      errors.push(...selectedDevices);
    }
  }

  return {
    successes,
    authErrors,
    errors,
  };
};

export const createServerIloFirmwareUpdateRequest = async (
  selectedDevices,
  iloFirmwareParams,
  startTime,
) => {
  const successes = [];
  const errors = [];
  const authErrors = [];

  const requestBody = {
    jobTemplateUri: UPDATE_ILO_FIRMWARE_SERVER_TEMPLATE_URI,
    data: {
      ilo_component: {},
    },
  };

  const scheduleRequestBody = {
    name: '',
    purpose: SERVER_ILO_FW_UPDATE,
    associatedResourceUri: '',
    operation: {
      type: 'REST',
      method: 'POST',
      uri: SCHEDULE_JOB_URL,
      body: requestBody,
    },
    schedule: {
      startAt: startTime,
    },
  };

  await Promise.allSettled(
    selectedDevices.map(device => {
      let payload;
      scheduleRequestBody.name = `${device?.name}`;
      requestBody.resourceUri = device.resourceUri;

      const iLOVersion = parseIloGeneration(device.iloFirmwareVersion_);
      const iLoComponent = iloFirmwareParams.find(
        x => x.comp_name === iLOVersion,
      );

      if (device.isIloFwUpdateTestVersionSupported_) {
        requestBody.data.ilo_component.filename = iLoComponent?.test_file_name;
        requestBody.data.ilo_component.version = iLoComponent?.test_version;
      } else {
        requestBody.data.ilo_component.filename = iLoComponent?.file_name;
        requestBody.data.ilo_component.version = iLoComponent?.version;
      }
      if (startTime) {
        scheduleRequestBody.associatedResourceUri = device.resourceUri;
        scheduleRequestBody.operation.body = requestBody;
        payload = scheduleRequestBody;
      } else {
        payload = requestBody;
      }
      return (
        startTime ? createSchedule({ ...payload }) : createJob({ ...payload })
      )
        .then(response => {
          if (response && response.status >= 200 && response.status <= 299) {
            successes.push(device);
          }
        })
        .catch(e => {
          if (e && e.response && e.response.status === 403) {
            authErrors.push(device);
          } else {
            errors.push(device);
          }
        });
    }),
  );

  return {
    successes,
    errors,
    authErrors,
  };
};

export const createServerBaselineUpdatRequest = async (
  selectedDevices,
  baseline,
  installSWDrivers,
  downgrade,
  powerOff,
  gen10Baseline,
  gen11Baseline,
  gen12Baseline,
  gen13Baseline,
  startTime,
  waitForReboot,
  rebootTimeout,
  isAnyDevicePoweredOn,
  cohesityFlag,
  baselineSelectionOption,
  jobStableApiFlag,
  installswdriversOverrides,
) => {
  const successes = [];
  const errors = [];
  const authErrors = [];

  const data = {
    bundle_id: baseline.id,
    install_sw_drivers: installSWDrivers,
    downgrade,
    is_preflightcheck_skipped: false,
  };

  // Handle custom baseline case
  if (baseline.componentExclusionsEnabled && baseline.settingId) {
    data.bundle_id = baseline.settingId;
    data.baseline_type = CUSTOM_BASELINE_TYPE;
  }

  if (cohesityFlag) {
    data.wait_for_power_off_or_reboot = waitForReboot;
    data.wait_for_power_off_or_reboot_timeout = rebootTimeout.value;
  }

  data.power_off = powerOff;
  data.skip_blocklisted_components = true;

  let toBundleName = '';

  await Promise.allSettled(
    selectedDevices.map(async device => {
      const hasInstallSwDriversOverride =
        installswdriversOverrides?.length > 0 &&
        installswdriversOverrides.includes(device.id);

      // Build device-specific request body
      let requestBody = jobStableApiFlag
        ? {
            jobTemplate: FIRMWARE_UPDATE_TEMPLATE_ID,
            resourceType: SERVER_RESOURCE_TYPE,
            resourceId: device.id,
            jobParams: { ...data },
          }
        : {
            jobTemplateUri: FIRMWARE_UPDATE_TEMPLATE_URI,
            resourceUri: device.resourceUri,
            data: { ...data },
          };

      // Get reference to params object (jobParams or data)
      const params = jobStableApiFlag
        ? requestBody.jobParams
        : requestBody.data;

      let apiCall = jobStableApiFlag ? createJobV1 : createJob;

      // Apply generation-specific baseline
      if (device.serverGeneration === GEN_10) {
        params.bundle_id = gen10Baseline?.id;
        toBundleName = gen10Baseline?.displayName;
        if (
          gen10Baseline?.componentExclusionsEnabled &&
          gen10Baseline?.settingId
        ) {
          params.bundle_id = gen10Baseline.settingId;
          params.baseline_type = CUSTOM_BASELINE_TYPE;
        }
      } else if (device.serverGeneration === GEN_11) {
        if (baselineSelectionOption === DOWNLOADED_BASELINE_OPTION) {
          /** JSON example for attemptedBaselineUri structure
           * {
           *  "attemptedBaselineDisplayName_": "SPP 2025.07.00.00 (08 Aug 2025)",
           *  "attemptedBaselineUri": "/v1/firmware-bundles/54cdea84c1704d9ebe42db8c210e4d04",
           * }
           */
          params.bundle_id =
            device?.lastFirmwareUpdate?.attemptedBaselineUri?.slice(
              device?.lastFirmwareUpdate?.attemptedBaselineUri.lastIndexOf(
                '/',
              ) + 1,
            );
          toBundleName =
            device?.lastFirmwareUpdate?.attemptedBaselineDisplayName_;
          params.downgrade = device?.lastFirmwareUpdate?.downgrade;
          params.install_sw_drivers =
            device?.lastFirmwareUpdate?.installSwDrivers;
        } else {
          params.bundle_id = gen11Baseline?.id;
          toBundleName = gen11Baseline?.displayName;
          if (
            gen11Baseline?.componentExclusionsEnabled &&
            gen11Baseline?.settingId
          ) {
            params.bundle_id = gen11Baseline.settingId;
            params.baseline_type = CUSTOM_BASELINE_TYPE;
          }
        }
      } else if (device.serverGeneration === GEN_12) {
        if (baselineSelectionOption === DOWNLOADED_BASELINE_OPTION) {
          params.bundle_id =
            device?.lastFirmwareUpdate?.attemptedBaselineUri?.slice(
              device?.lastFirmwareUpdate?.attemptedBaselineUri.lastIndexOf(
                '/',
              ) + 1,
            );
          toBundleName =
            device?.lastFirmwareUpdate?.attemptedBaselineDisplayName_;
          params.downgrade = device?.lastFirmwareUpdate?.downgrade;
          params.install_sw_drivers =
            device?.lastFirmwareUpdate?.installSwDrivers;
        } else {
          params.bundle_id = gen12Baseline?.id;
          toBundleName = gen12Baseline?.displayName;
          if (
            gen12Baseline?.componentExclusionsEnabled &&
            gen12Baseline?.settingId
          ) {
            params.bundle_id = gen12Baseline.settingId;
            params.baseline_type = CUSTOM_BASELINE_TYPE;
          }
        }
      } else if (device.serverGeneration === GEN_13) {
        if (baselineSelectionOption === DOWNLOADED_BASELINE_OPTION) {
          params.bundle_id =
            device?.lastFirmwareUpdate?.attemptedBaselineUri?.slice(
              device?.lastFirmwareUpdate?.attemptedBaselineUri.lastIndexOf(
                '/',
              ) + 1,
            );
          toBundleName =
            device?.lastFirmwareUpdate?.attemptedBaselineDisplayName_;
          params.downgrade = device?.lastFirmwareUpdate?.downgrade;
          params.install_sw_drivers =
            device?.lastFirmwareUpdate?.installSwDrivers;
        } else {
          if (!gen13Baseline?.id) {
            errors.push(device);
            return Promise.resolve();
          }
          params.bundle_id = gen13Baseline.id;
          toBundleName = gen13Baseline.displayName;
          if (
            gen13Baseline.componentExclusionsEnabled &&
            gen13Baseline.settingId
          ) {
            params.bundle_id = gen13Baseline.settingId;
            params.baseline_type = CUSTOM_BASELINE_TYPE;
          }
        }
      }
      if (hasInstallSwDriversOverride) {
        params.install_sw_drivers = false;
      }

      if (cohesityFlag && startTime) {
        const updateRequestBody = { ...requestBody };

        requestBody = {
          name: `${device?.name}`,
          purpose: SERVER_FW_UPDATE,
          description: JSON.stringify({
            to_bundle: toBundleName,
            is_any_device_powered_on: isAnyDevicePoweredOn,
          }),
          associatedResourceUri: device.resourceUri,
          operation: {
            type: 'REST',
            method: 'POST',
            uri: SCHEDULE_JOB_URL,
            body: updateRequestBody,
          },
          schedule: {
            startAt: startTime,
          },
        };

        apiCall = createSchedule;
      }

      return apiCall({
        ...requestBody,
      })
        .then(response => {
          if (response && response.status >= 200 && response.status <= 299) {
            successes.push(device);
          }
        })
        .catch(e => {
          if (e && e.response && e.response.status === 403) {
            authErrors.push(device);
          } else {
            errors.push(device);
          }
        });
    }),
  );

  return {
    successes,
    errors,
    authErrors,
  };
};

export const createApplianceUpdateRequest = async (
  selectedAppliances,
  firmware,
  applianceUriIdFixFlag,
) => {
  const successes = [];
  const errors = [];
  const authErrors = [];

  const requestBody = {
    jobTemplateUri: OV_FIRMWARE_UPDATE_TEMPLATE_URI,
    data: {
      applianceFirmwareId: firmware.id,
    },
  };

  await Promise.allSettled(
    selectedAppliances.map(appliance =>
      createJob({
        ...requestBody,
        // eslint-disable-next-line max-len
        resourceUri: `${URLS.OPS_MGMT_OV_APPLIANCES_URI}/${applianceUriIdFixFlag ? appliance.id : appliance.deviceId}`,
      })
        .then(response => {
          if (response && response.status >= 200 && response.status <= 299) {
            successes.push(appliance);
          }
        })
        .catch(e => {
          if (e?.response && e.response.status === 403) {
            authErrors.push(appliance);
          } else {
            errors.push(appliance);
          }
        }),
    ),
  );

  return {
    successes,
    errors,
    authErrors,
  };
};

export const createGroupApplianceUpdateRequest = async ({
  group,
  selectedAppliances,
}) => {
  const successes = [];
  const errors = [];
  const authErrors = [];
  const apiCall = createJob;
  const requestBody = {
    resourceUri: group?.resourceUri,
    jobTemplateUri: APPLIANCE_BATCH_UPDATE_TEMPLATE_URI,
    data: {
      targetApplianceIds: selectedAppliances.map(
        appliance => appliance.deviceId,
      ),
    },
  };
  let response;
  try {
    response = await apiCall({
      ...requestBody,
    });
    if (response && response.status >= 200 && response.status <= 299) {
      successes.push(...selectedAppliances);
    }
  } catch (e) {
    if (e?.response && e.response.status === 403) {
      authErrors.push(...selectedAppliances);
    } else {
      errors.push(...selectedAppliances);
    }
  }

  return {
    successes,
    authErrors,
    errors,
  };
};

export const createServerBaselineDownloadRequest = async (
  selectedDevices,
  baseline,
  installSWDrivers,
  downgrade,
  gen11Baseline,
  gen12Baseline,
  gen13Baseline,
  startTime,
) => {
  const successes = [];
  const errors = [];
  const authErrors = [];

  let apiCall = createJob;

  const commonRequestBody = {
    jobTemplateUri: SERVER_FIRMWARE_DOWNLOAD_TEMPLATE_URI,
    data: {
      bundle_id: baseline.id,
      install_sw_drivers: installSWDrivers,
      downgrade,
      is_preflightcheck_skipped: false,
    },
  };

  // Handle custom baseline case
  if (baseline.componentExclusionsEnabled && baseline.settingId) {
    commonRequestBody.data.bundle_id = baseline.settingId;
    commonRequestBody.data.baseline_type = CUSTOM_BASELINE_TYPE;
  }

  let toBundleName = '';

  await Promise.allSettled(
    selectedDevices.map(device => {
      let requestBody = { ...commonRequestBody };
      if (device.serverGeneration === GEN_11) {
        requestBody.data.bundle_id = gen11Baseline?.id;
        toBundleName = gen11Baseline?.displayName;
        // Handle custom baseline for Gen11
        if (
          gen11Baseline?.componentExclusionsEnabled &&
          gen11Baseline?.settingId
        ) {
          requestBody.data.bundle_id = gen11Baseline.settingId;
          requestBody.data.baseline_type = CUSTOM_BASELINE_TYPE;
        }
      } else if (device.serverGeneration === GEN_12) {
        requestBody.data.bundle_id = gen12Baseline?.id;
        toBundleName = gen12Baseline?.displayName;
        // Handle custom baseline for Gen12
        if (
          gen12Baseline?.componentExclusionsEnabled &&
          gen12Baseline?.settingId
        ) {
          requestBody.data.bundle_id = gen12Baseline.settingId;
          requestBody.data.baseline_type = CUSTOM_BASELINE_TYPE;
        }
      } else if (device.serverGeneration === GEN_13) {
        if (!gen13Baseline?.id) {
          errors.push(device);
          return Promise.resolve();
        }
        requestBody.data.bundle_id = gen13Baseline.id;
        toBundleName = gen13Baseline.displayName;
        // Handle custom baseline for Gen13
        if (
          gen13Baseline.componentExclusionsEnabled &&
          gen13Baseline.settingId
        ) {
          requestBody.data.bundle_id = gen13Baseline.settingId;
          requestBody.data.baseline_type = CUSTOM_BASELINE_TYPE;
        }
      }
      requestBody.resourceUri = device.resourceUri;

      if (startTime) {
        const updateRequestBody = { ...requestBody };

        requestBody = {
          name: `${device?.name}`,
          purpose: 'SERVER_FW_DOWNLOAD',
          description: JSON.stringify({
            to_bundle: toBundleName,
          }),
          associatedResourceUri: device.resourceUri,
          operation: {
            type: 'REST',
            method: 'POST',
            uri: SCHEDULE_JOB_URL,
            body: updateRequestBody,
          },
          schedule: {
            startAt: startTime,
          },
        };

        apiCall = createSchedule;
      }

      return apiCall({
        ...requestBody,
      })
        .then(response => {
          if (response && response.status >= 200 && response.status <= 299) {
            successes.push(device);
          }
        })
        .catch(e => {
          if (e?.response?.status === 403) {
            authErrors.push(device);
          } else {
            errors.push(device);
          }
        });
    }),
  );

  return {
    successes,
    errors,
    authErrors,
  };
};

export const createGroupBaselineDownloadRequest = async ({
  group,
  selectedDevices,
  installSWDrivers,
  downgrade,
  startTime,
}) => {
  const successes = [];
  const errors = [];
  const authErrors = [];
  let apiCall = createJob;
  let requestBody = {
    resourceUri: group?.resourceUri,
    jobTemplateUri: GROUP_FIRMWARE_DOWNLOAD_TEMPLATE_URI,
    data: {
      devices: selectedDevices.map(dev => dev.id),
      installSWDrivers,
      downgrade,
    },
  };

  if (startTime) {
    const groupBaselineUpdateRequestBody = { ...requestBody };

    requestBody = {
      name: `${group?.name}`,
      purpose: 'GROUP_FW_DOWNLOAD',
      associatedResourceUri: group?.resourceUri,
      operation: {
        type: 'REST',
        method: 'POST',
        uri: SCHEDULE_JOB_URL,
        body: groupBaselineUpdateRequestBody,
      },
      schedule: {
        startAt: startTime,
      },
    };
    apiCall = createSchedule;
  }

  let response;
  try {
    response = await apiCall({
      ...requestBody,
    });
    if (response && response.status >= 200 && response.status <= 299) {
      successes.push(...selectedDevices);
    }
  } catch (e) {
    if (e?.response?.status === 403) {
      authErrors.push(...selectedDevices);
    } else {
      errors.push(...selectedDevices);
    }
  }

  return {
    successes,
    authErrors,
    errors,
  };
};

export const queryUserPermissions = () => axios.get(URLS.USER_PERMISSIONS_URL);

export const queryTenantUserPermissions = async tenantAcid =>
  axios.get(URLS.USER_PERMISSIONS_URL, {
    forceTenantAcid: tenantAcid,
  });

export const querySBAC = servers => {
  const payload = {
    operation_type: 'EDIT_SERVER_OPERATION',
    resources: servers.map(server => server.id),
  };

  return axios.post(URLS.SBAC_CHECK_URL, payload);
};

export function getPreferences(useNewGlcpPreferencesApi = false) {
  const preferencesUrl = useNewGlcpPreferencesApi
    ? URLS.GLOBAL_PREFERENCES_URL
    : URLS.PREFERENCES_URL;
  return axios.get(`${preferencesUrl}`);
}

export function getProvisionedRegions() {
  return axios.get(`${URLS.PROVISIONED_REGIONS_URL}`);
}

export function fetchRegionCounts(region) {
  const apiPath = window.indigo?.REACT_APP_COM_API_PATH || '';
  const regionApiRoot = `${region.url}${apiPath}`;
  const url = URLS.SERVERS_COUNTS_URL.replace(
    URLS.COM_API_ROOT_URL,
    regionApiRoot,
  );
  return axios.get(url, { params: { fields: ['healthSummary_'] } });
}

export function getAccountType(acid) {
  return axios.get(`${URLS.MSP_URL}/${acid}`, {
    // define a custom config value for the interceptor to skip adding the tenant acid
    omitTenantAcid: true,
  });
}

export function getTenants(acid) {
  return axios.get(`${URLS.MSP_URL}/${acid}/tenants`, {
    // define a custom config value for the interceptor to skip adding the tenant acid
    omitTenantAcid: true,
  });
}

export const getLocationConfiguration = async serverId =>
  axios.get(`${URLS.SDI_CONFIG_URL}/${serverId}`);

export const getSdiLocations = () => axios.get(URLS.SDI_LOCATIONS_URL);

export function getAppliances(
  offset,
  limit,
  sort,
  searchString,
  filterQueries,
) {
  const params = {};
  const filters = filterQueries || [];

  offset && (params.offset = offset);
  limit && (params.limit = limit);
  sort && (params.sort = sort);
  searchString &&
    (params.search = `${searchString
      .trim()
      .replaceAll('\\', '\\\\')
      .replaceAll("'", "\\'")}`);

  if (filters && filters.length > 0) {
    if (filters instanceof Array) {
      params.filter = `(${filters.join(') and (')})`;
    } else {
      params.filter = filters;
    }
  }

  return axios.get(`${URLS.APPLIANCES_UIDOORWAY_URL}`, {
    params,
  });
}

export async function getAllAppliances(sort, searchString, filterQueries) {
  const includePaginationMetaData = true;
  return getAllPages(
    (offset, limit) =>
      getAppliances(offset, limit, sort, searchString, filterQueries),
    COUNTS.MAX_IRIS_COUNT,
    includePaginationMetaData,
  );
}

export async function getAllAppliancesInGroup(
  groupId,
  sort = null,
  filterQueries = [],
) {
  const localFilterQueries = [`group/id eq '${groupId}'`, ...filterQueries];
  const applianceData = await getAllAppliances(sort, null, localFilterQueries);
  const appliances = applianceData.data.items;
  return appliances;
}

export async function getAllSelectedAppliances(
  selected,
  unselected,
  allSelected,
  sort,
  searchString,
  filterQueries,
) {
  const appliances = [];
  const appliancesData = {};

  if (
    allSelected !== true &&
    selected &&
    selected.length > 0 &&
    selected.length < COUNTS.MAX_FILTER_GET_ALL_APPLIANCES
  ) {
    const selectedQueries = [];
    selected.forEach(id => {
      selectedQueries.push(`'${id}'`);
    });
    let queriesToAdd = `${selectedQueries.join()}`;
    queriesToAdd = `id in (${queriesToAdd})`;
    queriesToAdd.length <= COUNTS.MAX_FILTER_GET_ALL_APPLIANCES &&
      filterQueries &&
      filterQueries.splice(0, filterQueries.length, queriesToAdd);
  }

  const includePaginationMetaData = true;
  const results = await getAllPages(
    (offset, limit) =>
      getAppliances(offset, limit, sort, searchString, filterQueries),
    COUNTS.MAX_IRIS_COUNT,
    includePaginationMetaData,
  );

  if (results?.data?.items) {
    results.data.items.forEach(appliance => {
      if (isSelected(appliance.id, selected, unselected, allSelected)) {
        appliances.push(appliance);
      }
    });
  }

  appliancesData.data = {
    items: appliances,
    offset: 0,
    total: appliances.length,
    count: appliances.length,
  };

  return appliancesData;
}

export function getApplianceSettingsTemplate(id) {
  return axios.get(`${URLS.OPS_MGMT_APPLIANCE_SETTINGS_TEMPLATE_URL}/${id}`);
}

export function getApplianceSettingsTemplateCounts() {
  return axios.get(URLS.APPLIANCE_SETTINGS_COUNTS_URL);
}

export function getApplianceById(id) {
  return axios.get(`${URLS.APPLIANCES_UIDOORWAY_URL}/${id}`);
}

export function getAppliancesStateCounts() {
  const fields = ['state'];
  return axios.get(URLS.APPLIANCES_COUNTS_UIDOORWAY_URL, {
    params: { fields },
  });
}

export function getAppliancesCounts() {
  const fields = ['state', 'healthSummary_'];
  return axios.get(URLS.APPLIANCES_COUNTS_UIDOORWAY_URL, {
    params: { fields },
  });
}

export function getServersCountsByApplianceId(
  applianceId,
  serverEndpointsFlag,
) {
  if (serverEndpointsFlag) {
    return axios.get(
      `${URLS.OPS_MGMT_OV_SERVER_HW_URL}?limit=1&filter=eq(applianceId,'${applianceId}')`,
    );
  }
  return axios.get(
    `${URLS.OPS_MGMT_OV_RESOURCES_URL}?` +
      `limit=1&filter=and(${SERVERS_CATEGORY_FILTER},eq(applianceId,'${applianceId}'))`,
  );
}

export function getActivationKey(payload) {
  return axios.post(
    `${URLS.OPS_MGMT_OV_APPLIANCES_URL}/activation-key`,
    payload,
  );
}

export function addAppliance(payload) {
  return axios.post(`${URLS.OPS_MGMT_OV_APPLIANCES_URL}`, payload);
}

export function checkApplianceSettingsCompliance(id) {
  return axios.post(`${URLS.OPS_MGMT_OV_APPLIANCES_URL}/${id}/compliance`, {});
}

export function getApplianceSettingsCompliance(id, complianceId) {
  return axios.get(
    `${URLS.OPS_MGMT_OV_APPLIANCES_URL}/${id}/compliance/${complianceId}`,
  );
}

export const removeAppliance = async (id, type) => {
  if (type === ONE_VIEW) {
    return axios.delete(`${URLS.OPS_MGMT_OV_APPLIANCES_URL}/${id}`);
  }
  return axios.delete(`${URLS.OPS_MGMT_GATEWAY_APPLIANCE_URL}/${id}`);
};

export const removeMgmtAppliance = async id =>
  axios.delete(`${URLS.OPS_MGMT_OV_APPLIANCES_URL}/${id}`);

export function updateJob(id, payload) {
  return axios.patch(`${URLS.JOBS_URL}/${id}`, payload);
}

// Get a page of appliance settings templates
export function getApplianceSettingsTemplates(
  offset,
  limit,
  sort,
  filterQueries,
) {
  const params = {};
  const filters = filterQueries || [];
  offset && (params.offset = offset);
  limit && (params.limit = limit);
  sort && (params.sort = sort);

  if (filters && filters.length > 0) {
    if (filters instanceof Array) {
      params.filter = `and(${filters.join(',')})`;
    } else {
      params.filter = filters;
    }
  }

  return axios.get(URLS.OPS_MGMT_APPLIANCE_SETTINGS_TEMPLATE_URL, {
    params,
  });
}

export function createApplianceSettingTemplates(payload) {
  return axios.post(URLS.OPS_MGMT_APPLIANCE_SETTINGS_TEMPLATE_URL, payload);
}

export function applyApplianceSettingsTemplate(id, payload) {
  return axios.post(
    `${URLS.OPS_MGMT_APPLIANCE_SETTINGS_TEMPLATE_URL}/${id}/apply`,
    payload,
  );
}

export function deleteApplianceSettingsTemplate(id) {
  return axios.delete(`${URLS.OPS_MGMT_APPLIANCE_SETTINGS_TEMPLATE_URL}/${id}`);
}

export const getInventory = async (
  applianceId,
  uri,
  serverEndpointsFlag,
  tenantAcid,
) => {
  if (serverEndpointsFlag) {
    return axios.get(`${URLS.OPS_MGMT_OV_SERVER_HW_URL}/${applianceId}${uri}`, {
      forceTenantAcid: tenantAcid,
    });
  }
  return axios.get(
    // eslint-disable-next-line max-len
    `${URLS.OPS_MGMT_OV_SERVER_HW_URL}?filter=and(eq(applianceId,'${applianceId}'),eq(uri,'${uri}'))`,
    { forceTenantAcid: tenantAcid },
  );
};

export const getInventoryStatus = async (filter, serverEndpointsFlag) => {
  const queryString = filter ? `?filter=${filter}` : '';
  if (serverEndpointsFlag) {
    return axios.get(
      `${URLS.OPS_MGMT_OV_SERVER_HW_COUNTS_URL}/status${queryString}`,
    );
  }
  return axios.get(
    `${URLS.OPS_MGMT_OV_HARDWARE_COUNTS_URL}/status${queryString}`,
  );
};

export const getInventoriesByCategory = async (
  category,
  offset,
  limit,
  sort,
  searchString,
  filterQueries,
  serverEndpointsFlag,
) => {
  const params = {};
  offset && (params.offset = offset);
  limit && (params.limit = limit);
  sort && (params.sort = sort);
  searchString &&
    (params.search = `${searchString
      .trim()
      .replaceAll('\\', '\\\\')
      .replaceAll("'", "\\'")}`);
  const filters = filterQueries || [];

  if (filters && filters.length > 0) {
    params.filter = `and(${filters.join(',')})`;
  }
  if (serverEndpointsFlag) {
    return axios.get(`${URLS.OPS_MGMT_OV_SERVER_HW_URL}?`, { params });
  }

  return axios.get(`${URLS.OPS_MGMT_OV_RESOURCES_URL}?`, { params });
};

export async function getAllInventories(sort, filterQueries) {
  return getAllPages(
    (offset, limit) =>
      getInventoriesByCategory(
        null,
        offset,
        limit,
        sort,
        null,
        filterQueries,
        null,
      ),
    COUNTS.MAX_IRIS_INVENTORY_COUNT,
  );
}

export const getServerProfile = async (
  applianceId,
  uri,
  serverEndpointsFlag,
) => {
  if (serverEndpointsFlag) {
    return axios.get(
      `${URLS.OPS_MGMT_OV_SERVER_PROFILE_URL}/${applianceId}${uri}`,
    );
  }
  return axios.get(
    // eslint-disable-next-line max-len
    `${URLS.OPS_MGMT_OV_RESOURCES_URL}?filter=and(eq(applianceId,'${applianceId}'),eq(uri,'${uri}'))`,
  );
};

export const getProfileStatus = async (filter, serverEndpointsFlag) => {
  const queryString = filter ? `?filter=${filter}` : '';
  if (serverEndpointsFlag) {
    return axios.get(`${URLS.OPS_MGMT_OV_SERVER_PROFILE_COUNTS_URL}/status`);
  }
  return axios.get(
    `${URLS.OPS_MGMT_OV_PROFILE_COUNTS_URL}/status${queryString}`,
  );
};

export const getServerProfiles = async (
  offset,
  limit,
  sort,
  searchString,
  filterQueries,
  serverEndpointsFlag,
) => {
  const params = {};
  offset && (params.offset = offset);
  limit && (params.limit = limit);
  sort && (params.sort = sort);
  searchString &&
    (params.search = `${searchString
      .trim()
      .replaceAll('\\', '\\\\')
      .replaceAll("'", "\\'")}`);
  const filters = filterQueries || [];

  if (filters && filters.length > 0) {
    params.filter = `and(${filters.join(',')})`;
  }
  if (serverEndpointsFlag) {
    return axios.get(`${URLS.OPS_MGMT_OV_SERVER_PROFILE_URL}?`, { params });
  }
  return axios.get(`${URLS.OPS_MGMT_OV_RESOURCES_URL}?`, { params });
};

export function getAllExternalServices(
  serviceType,
  isDoorwayApi = false,
  offset,
  limit,
) {
  const url = isDoorwayApi
    ? URLS.EXTERNAL_SERVICE_EXPORT_URL
    : URLS.OPS_MGMT_EXTERNAL_SERVICE_URL;
  const params = {};
  isDoorwayApi && offset && (params.offset = offset);
  isDoorwayApi && limit && (params.limit = limit);
  return axios.get(`${url}?filter=serviceType eq '${serviceType}'`, { params });
}

export function getExternalServiceById(id, isDoorwayApi = false) {
  const url = isDoorwayApi
    ? URLS.EXTERNAL_SERVICE_EXPORT_URL
    : URLS.OPS_MGMT_EXTERNAL_SERVICE_URL;
  return axios.get(`${url}/${id}`);
}

export function getExternalServiceActivities(externalServiceId) {
  const NUM_ITEMS_IN_EXTERNAL_SERVICE_ACTIVITY_CARD = 10;
  const params = {};
  params.offset = 0;
  params.limit = NUM_ITEMS_IN_EXTERNAL_SERVICE_ACTIVITY_CARD;
  params.filter = "(associatedServerId eq '" + `${externalServiceId}')`;

  return axios.get(URLS.UI_DOORWAY_ACTIVITIES_URL, {
    params,
  });
}

export function removeExternalService(id) {
  return axios.delete(`${URLS.OPS_MGMT_EXTERNAL_SERVICE_URL}/${id}`);
}

export function removeVcenter(payload) {
  return axios.post(URLS.JOBS_URL, payload);
}

export function synchFirmwareBaselineForVcenter(payload) {
  return axios.post(URLS.JOBS_URL, payload);
}

export function getLastVcenterRegistrationActivityStatus(vCenterId) {
  const params = {};
  params.limit = 1;
  // eslint-disable-next-line max-len
  params.filter = `(associatedServerId  eq '${vCenterId}' and name eq 'External service configuration')`;

  return axios.get(URLS.UI_DOORWAY_ACTIVITIES_URL, {
    params,
  });
}

export function testExternalService(id) {
  return axios.post(`${URLS.OPS_MGMT_EXTERNAL_SERVICE_URL}/${id}/test`, {});
}

export function editServiceNowConfig(
  id,
  incidentUrl,
  oauthUrl,
  clientId,
  clientSecret,
  refreshToken,
  refreshTokenExpiry,
  incidentCreationScopeData,
  hasAuthenticationChanged,
) {
  const reqBody = {
    name: SERVICE_NOW_NAME,

    serviceData: {
      oauthUrl,
      incidentUrl,
      refreshTokenExpiryInDays: parseInt(refreshTokenExpiry, 10),
    },
  };
  reqBody.serviceData.incidentCreationScope = incidentCreationScopeData;
  if (hasAuthenticationChanged) {
    reqBody.authentication = {
      clientId,
      clientSecret,
      refreshToken,
    };
  }
  return axios.patch(`${URLS.OPS_MGMT_EXTERNAL_SERVICE_URL}/${id}`, reqBody, {
    headers: { 'content-type': 'application/merge-patch+json' },
  });
}

export function configureServiceNow(
  incidentUrl,
  oauthUrl,
  clientId,
  clientSecret,
  refreshToken,
  refreshTokenExpiry,
  incidentCreationScopeData,
) {
  const reqBody = {
    name: SERVICE_NOW_NAME,
    serviceType: SERVICE_NOW_TYPE,
    authenticationType: OAUTH,
    authentication: {
      clientId,
      clientSecret,
      refreshToken,
    },
    serviceData: {
      oauthUrl,
      incidentUrl,
      refreshTokenExpiryInDays: parseInt(refreshTokenExpiry, 10),
    },
  };
  reqBody.serviceData.incidentCreationScope = incidentCreationScopeData;
  return axios.post(URLS.OPS_MGMT_EXTERNAL_SERVICE_URL, reqBody);
}

export function configureArubaCentral(
  state,
  nbUrl,
  clientId,
  clientSecret,
  refreshToken,
) {
  const reqBody = {
    name: ARUBA_CENTRAL_NAME,
    serviceType: ARUBA_CENTRAL_TYPE,
    authenticationType: OAUTH,
    state,
    authentication: {
      clientId,
      clientSecret,
      refreshToken,
    },
    serviceData: { nbUrl },
  };
  return axios.post(URLS.OPS_MGMT_EXTERNAL_SERVICE_URL, reqBody);
}

export function editArubaCentralConfig(
  id,
  state,
  nbUrl,
  clientId,
  clientSecret,
  refreshToken,
) {
  const reqBody = {
    authentication: { clientId },
    serviceData: { nbUrl },
    state,
  };
  if (clientSecret) {
    reqBody.authentication.clientSecret = clientSecret;
  }
  if (refreshToken) {
    reqBody.authentication.refreshToken = refreshToken;
  }
  return axios.patch(`${URLS.OPS_MGMT_EXTERNAL_SERVICE_URL}/${id}`, reqBody, {
    headers: { 'content-type': 'application/merge-patch+json' },
  });
}

export const getDsccIntegration = async () => getAllExternalServices(DSCC_TYPE);

export function editDsccConfig(id, formValues) {
  const reqBody = {
    authentication: {
      clientId: formValues?.clientId,
      clientSecret: formValues?.clientSecret,
    },
    state: formValues?.state,
    ...(formValues?.region
      ? { serviceData: { region: formValues.region } }
      : {}),
  };

  if (!formValues?.clientId || !formValues?.clientSecret) {
    delete reqBody?.authentication;
  }
  return axios.patch(`${URLS.OPS_MGMT_EXTERNAL_SERVICE_URL}/${id}`, reqBody, {
    headers: { 'content-type': 'application/merge-patch+json' },
  });
}

export function configureDscc(formValues) {
  const reqBody = {
    name: DSCC_NAME,
    serviceType: DSCC_TYPE,
    authenticationType: OAUTH,
    authentication: {
      clientId: formValues?.clientId,
      clientSecret: formValues?.clientSecret,
    },
    state: formValues?.state || 'ENABLED',
    ...(formValues?.region
      ? { serviceData: { region: formValues.region } }
      : {}),
  };

  return axios.post(URLS.OPS_MGMT_EXTERNAL_SERVICE_URL, reqBody);
}

export function configureMetricsData(
  flag,
  powerThresholdAlerts,
  powerUtilizationThresholdPercentage,
  grouplist,
  resourceUri = null,
  filterTags = null,
) {
  const reqBody = {
    metricsCollection: flag ? 'SYSTEM_SCHEDULED' : 'ON_DEMAND',
  };
  reqBody.powerThresholdAlerts = powerThresholdAlerts;
  reqBody.powerUtilizationThresholdPercentage =
    powerUtilizationThresholdPercentage;
  reqBody.alertResources = grouplist;
  if (resourceUri) reqBody.resourceUri = resourceUri;
  if (filterTags) reqBody.filterTags = filterTags;

  return axios.post(`${URLS.METRICS_DATA_URL_V1}`, reqBody);
}

export function getMetricsData() {
  return axios.get(`${URLS.METRICS_DATA_URL_V1}`);
}

export function getMetricsDataRange(reportType) {
  return axios.get(`${URLS.METRICS_DATA_RANGE}?reportType=${reportType}`);
}

export function getMetricsDataRangeForUtilization(metricType) {
  return axios.get(
    `${URLS.METRICS_DATA_RANGE}?metric-type=${UTILIZATION_METRIC_TYPE_MAP.CPU_UTILIZATION}`,
  );
}

export function getSustainabilityGraphDetails(
  startDate,
  endDate,
  projectionDays,
  resourceUri = ALL_SERVERS_FILTER_MGMT_URL,
  sampleSize = null,
  filterTags = null,
) {
  const params = {};
  if (startDate) params['start-date'] = startDate;
  if (endDate) params['end-date'] = endDate;
  if (projectionDays) params['projection-days'] = projectionDays;
  if (filterTags) {
    params['filter-tags'] = filterTags;
  } else if (resourceUri) {
    params['resource-uri'] = resourceUri;
  }
  if (sampleSize) params['sample-size'] = sampleSize;

  return axios.get(`${URLS.ENERGY_OVER_TIME}`, { params });
}

export function getAllServerUtilizationAverageDetails(
  startDate,
  endDate,
  resourceUri = ALL_SERVERS_FILTER_MGMT_URL,
  filterTags = null,
  sampleSize,
) {
  const searchParams = new URLSearchParams();

  if (startDate) searchParams.append('start-date', startDate);
  if (endDate) searchParams.append('end-date', endDate);
  if (filterTags) {
    searchParams.append('filter-tags', filterTags);
  } else if (resourceUri) {
    searchParams.append('resource-uri', resourceUri);
  }
  if (sampleSize) searchParams.append('sample-size', sampleSize);

  searchParams.append(
    'metric-type',
    UTILIZATION_METRIC_TYPE_MAP.CPU_UTILIZATION,
  );

  searchParams.append(
    'metric-type',
    UTILIZATION_METRIC_TYPE_MAP.MEMORY_BUS_UTILIZATION,
  );
  searchParams.append(
    'metric-type',
    UTILIZATION_METRIC_TYPE_MAP.IO_BUS_UTILIZATION,
  );
  searchParams.append(
    'metric-type',
    UTILIZATION_METRIC_TYPE_MAP.CPU_INTERCONNECT_UTILIZATION,
  );

  const queryString = searchParams.toString();
  const url = `${URLS.UTILIZATION_AVERAGE_OVER_TIME}?${queryString}`;

  return axios.get(url);
}
export function updateMetricsData(
  flag,
  id,
  powerThresholdAlerts,
  powerUtilizationThresholdPercentage,
  grouplist,
  resourceUri = null,
  filterTags = null,
) {
  const reqBody = {
    metricsCollection: flag ? 'SYSTEM_SCHEDULED' : 'ON_DEMAND',
  };
  reqBody.powerThresholdAlerts = powerThresholdAlerts;
  reqBody.powerUtilizationThresholdPercentage =
    powerUtilizationThresholdPercentage;
  reqBody.alertResources = grouplist;
  if (resourceUri) reqBody.resourceUri = resourceUri;
  if (filterTags) reqBody.filterTags = filterTags;

  return axios.patch(`${URLS.METRICS_DATA_URL_V1}/${id}`, reqBody, {
    headers: { 'content-type': 'application/merge-patch+json' },
  });
}

export async function getApplianceAlertsV2(applianceId, offset, limit) {
  const params = {};
  params.offset = offset || 0;
  params.limit = limit || COUNTS.MAX_DOORWAY_COUNT;
  params.sort = 'severity asc';
  params.filter = "(severity in ('CRITICAL','WARNING'))";

  return axios.get(`${URLS.APPLIANCES_UIDOORWAY_URL}/${applianceId}/alerts`, {
    params,
  });
}

export async function getAllApplianceAlertsV2(applianceId) {
  const includePaginationMetaData = true;
  return getAllPages(
    (offset, limit) => getApplianceAlertsV2(applianceId, offset, limit),
    COUNTS.MAX_DOORWAY_COUNT,
    includePaginationMetaData,
  );
}

export async function getHardwareAlerts(
  applianceId,
  hardwareUri,
  offset,
  limit,
) {
  const params = {};
  params.offset = offset || 0;
  params.limit = limit || COUNTS.MAX_IRIS_INVENTORY_COUNT;
  params.sort = 'status:asc';
  // eslint-disable-next-line max-len
  params.filter = `and(eq(category,'alerts'),in(state,'Active','Locked'),in(status,'Critical','Warning'),eq(applianceId,'${applianceId}'),eq(attributes.resourceUri,'${hardwareUri}'))`;

  return axios.get(`${URLS.OPS_MGMT_OV_RESOURCES_URL}`, { params });
}

export async function getHardwareAlertsV2(hardwareId, offset, limit) {
  const params = {};
  params.offset = offset || 0;
  params.limit = limit || COUNTS.MAX_DOORWAY_COUNT;
  params.sort = 'severity asc';
  params.filter = "(severity in ('CRITICAL','WARNING'))";

  return axios.get(`${URLS.SERVERS_URL}/${hardwareId}/alerts`, {
    params,
  });
}

export async function getAllHardwareAlertsV2(hardwareId) {
  return getAllPages(
    (offset, limit) => getHardwareAlertsV2(hardwareId, offset, limit),
    COUNTS.MAX_DOORWAY_COUNT,
  );
}

export async function getAllHardwareAlerts(applianceId, hardwareUri) {
  return getAllPages(
    (offset, limit) =>
      getHardwareAlerts(applianceId, hardwareUri, offset, limit),
    COUNTS.MAX_IRIS_INVENTORY_COUNT,
  );
}

export const getServerProfileData = async (
  applianceId,
  serverEndpointsFlag,
  serverHardwareUri,
) => {
  if (serverEndpointsFlag) {
    return axios.get(
      // eslint-disable-next-line max-len
      `${
        URLS.OPS_MGMT_OV_SERVER_PROFILE_URL
        // eslint-disable-next-line max-len
      }?filter=and(eq(applianceId,'${applianceId}'),eq(attributes.serverHardwareUri,'${serverHardwareUri}'))`,
    );
  }
  return axios.get(
    // eslint-disable-next-line max-len
    `${
      URLS.OPS_MGMT_OV_RESOURCES_URL
      // eslint-disable-next-line max-len
    }?filter=and(eq(applianceId,'${applianceId}'),eq(attributes.serverHardwareUri,'${serverHardwareUri}'),eq(category,'server-profiles'))`,
  );
};

export const getJobById = async id => axios.get(`${URLS.JOBS_URL}/${id}`);

export const getJobByIdV1 = async id =>
  axios.get(`${URLS.COMPUTE_OPS_MGMT_JOBS_URL_V1}/${id}`);

export const getJobByIdV1B2 = async id =>
  axios.get(`${URLS.COMPUTE_OPS_MGMT_JOBS_URL}/${id}`);

export const getJobs = async filter => {
  const queryString = filter ? `?filter=${filter}` : '';
  return axios.get(`${URLS.JOBS_URL}${queryString}`);
};

export const getJobsV1B2 = async filter => {
  const queryString = filter ? `?filter=${filter}` : '';
  return axios.get(`${URLS.COMPUTE_OPS_MGMT_JOBS_URL}${queryString}`);
};

export const getJobsWithLimit = async (
  filter = '',
  limit = 25,
  offset = 0,
  jobStableApiFlag,
) => {
  const params = new URLSearchParams();
  if (filter) params.append('filter', filter);
  params.append('limit', limit);
  params.append('offset', offset);
  const queryString = params.toString() ? `?${params.toString()}` : '';

  const url = `${
    jobStableApiFlag
      ? URLS.COMPUTE_OPS_MGMT_JOBS_URL_V1
      : URLS.COMPUTE_OPS_MGMT_JOBS_URL
  }${queryString}`;

  return axios.get(url);
};

export const getAllJobsWithLimit = async (
  filter = '',
  defaultLimit = 25,
  jobStableApiFlag,
) => {
  const includePaginationMetaData = true;

  return getAllPages(
    (offset, limit) =>
      getJobsWithLimit(filter, limit, offset, jobStableApiFlag),
    defaultLimit,
    includePaginationMetaData,
  );
};

export const createViewHardwareInventoryJob = (
  hardwareId,
  applianceId,
  togglePowerOn,
) => {
  const jobTemplateUri = SERVER_INVENTORY_TEMPLATE_URI;
  const requestBody = {
    // eslint-disable-next-line max-len
    resourceUri: `${URLS.OPS_MGMT_OV_APPLIANCES_URI}/${applianceId}/rest/server-hardware/${hardwareId}`,
    jobTemplateUri: `${jobTemplateUri}`,
    data: {
      power_on_server: togglePowerOn,
      is_activity_message_needed: true,
    },
  };
  requestBody.resourceUri = `/compute-ops-mgmt/v1beta2/servers/${hardwareId}`;
  const promise = createJobV1B2({
    ...requestBody,
  });

  return promise;
};

export const createViewServerInventoryJob = (serverId, togglePowerOn) => {
  const requestBody = {
    resourceUri: `/compute-ops-mgmt/v1beta2/servers/${serverId}`,
    jobTemplateUri: `${SERVER_INVENTORY_TEMPLATE_URI}`,
    data: {
      power_on_server: togglePowerOn,
      is_activity_message_needed: true,
    },
  };
  const promise = createJobV1B2({
    ...requestBody,
  });

  return promise;
};

export const getExistingNetworkConnectivityJob = async serverId => {
  // replace the '+' in serverId with url encoding %2B
  const serverIdEncoded = serverId.replace(/\+/g, '%2B');
  const networkConnectivityJobTemplateId = getIdFromUri(
    NETWORK_CONNECTIVITY_TEMPLATE_URI,
  );
  // eslint-disable-next-line max-len
  const filter = `(associated_resource_uri='/api/compute/v1/servers/${serverIdEncoded}' and job_template_id='${networkConnectivityJobTemplateId}' and (state='${JOB_STATE.PENDING.toLowerCase()}' or state='${JOB_STATE.RUNNING.toLowerCase()}'))`;
  return getJobs(filter);
};

export const createViewNeworkConnectivityJob = serverId =>
  createJob({
    resourceUri: `/api/compute/v1/servers/${serverId}`,
    jobTemplateUri: `${NETWORK_CONNECTIVITY_TEMPLATE_URI}`,
  });

export const createCheckExternalStorageComplianceJob = groupUri =>
  createJob({
    resourceUri: groupUri,
    jobTemplateUri: EXTERNAL_STORAGE_COMPLIANCE_TEMPLATE_URI,
  });

export const getExternalStorageCompliance = (groupId, esComplianceFlag) =>
  axios.get(
    // eslint-disable-next-line max-len
    `${esComplianceFlag ? URLS.OPS_MGMT_GROUPS_V1_URL : URLS.GROUPS_URL_V2}/${groupId}/external-storage-compliance`,
  );

export const getOVSubscription = () =>
  axios.get(URLS.ONEVIEW_SUBSCRIPTIONS_URL);

export async function getDeviceSubscriptions(limit, filters, sort) {
  const params = {};
  if (limit) params.limit = limit;
  if (filters) params.filter = `(${filters.join(') and (')})`;
  if (sort) params.sort = sort;
  return axios.get(URLS.SUBSCRIPTION_URL, { params });
}

export const getDeviceSubscriptionCounts = () =>
  axios.get(URLS.SUBSCRIPTION_POOL_URL);

// for offset based call
export function getSubscriptionKeys(
  offset,
  limit,
  sort,
  searchString,
  filterQueries,
) {
  const params = {};
  const filters = filterQueries || [];

  offset && (params.offset = offset);
  limit && (params.limit = limit);
  sort && (params.sort = sort);
  searchString &&
    (params.search = `${searchString
      .trim()
      .replaceAll('\\', '\\\\')
      .replaceAll("'", "\\'")}`);

  if (filters && filters.length > 0) {
    if (filters instanceof Array) {
      params.filter = `(${filters.join(') and (')})`;
    } else {
      params.filter = filters;
    }
  }

  return axios.get(URLS.SUBSCRIPTION_URL, { params });
}
export async function getSubscriptionKeyAvailability(key) {
  return axios.get(`${URLS.SUBSCRIPTION_URL}/${key}`);
}

export async function registerSubscriptionKey(key) {
  return axios.post(`${URLS.SUBSCRIPTION_KEY_REGISTRATION_URL}/${key}`, {});
}

export async function getAllSubscriptionKeys(
  sort,
  searchString,
  filterQueries,
) {
  const includePaginationMetaData = true;
  return getAllPages(
    (offset, limit) =>
      getSubscriptionKeys(offset, limit, sort, searchString, filterQueries),
    COUNTS.MAX_DOORWAY_COUNT,
    includePaginationMetaData,
  );
}

export const getAutoSubscriptionStatus = () =>
  axios.get(`${URLS.SUBSCRIPTION_URL}/auto-subscriptions`);

export * from './api/reports';

export function createReport(payload) {
  return axios.post(URLS.COMPUTE_OPS_MGMT_REPORTS_URL, payload);
}

export function getGlpTags(offset, limit, sort, searchString, filterQueries) {
  const params = { offset: null };
  const filters = filterQueries || [];

  offset && (params.offset = offset);
  limit && (params.limit = limit);
  sort && (params.sort = sort);

  if (filters && filters.length > 0) {
    if (filters instanceof Array) {
      params.filter = `(${filters.join(') and (')})`;
    } else {
      params.filter = filters;
    }
  }

  return axios.get(`${URLS.GLP_TAGS_URL}`, { params });
}
export async function getAllGlpTags(sort, searchString, filterQueries) {
  const includePaginationMetaData = true;
  return getAllPages(
    (offset, limit) =>
      getGlpTags(offset, limit, sort, searchString, filterQueries),
    COUNTS.MAX_GET_TAGS_V1_COUNT,
    includePaginationMetaData,
  );
}

export const getApplianceFilterInfo = (next, limit) => {
  const params = {};
  if (limit) params.limit = limit;
  if (next) params.next = next;
  return axios.get(URLS.APPLIANCES_URL, {
    params,
  });
};

export async function getAllApplianceFilterInfo() {
  const applianceFilterInfo = [];
  let first = true;
  let next = null;
  let fetch = null;
  let limit = COUNTS.MAX_DOORWAY_COUNT;

  while (first || next) {
    // do not attempt to fetch beyond opensearch's max limit
    if (applianceFilterInfo.length >= MAX_ITEMS) break;
    if (applianceFilterInfo.length + limit > MAX_ITEMS) {
      limit = MAX_ITEMS - applianceFilterInfo.length;
    }

    // Must wait for each response to get the
    // next parameter for the next request.
    // eslint-disable-next-line no-await-in-loop
    fetch = await getApplianceFilterInfo(next, limit).catch(e => {
      throw e;
    });
    next = fetch?.data?.next ? fetch.data.next : null;
    if (fetch?.data?.items) {
      fetch.data.items.forEach(applianceInfo => {
        applianceFilterInfo.push(applianceInfo);
      });
    }
    first = false;
  }
  return {
    data: {
      next: null,
      count: applianceFilterInfo.length,
      items: applianceFilterInfo,
    },
  };
}

export async function getOvSettingsByApplianceId(applianceId) {
  return axios.get(
    `${URLS.OV_APPLIANCE_SETTINGS_URL}?filter=and(eq(applianceId,'${applianceId}'))`,
  );
}

export async function getDeviceActivationKey(
  subscriptionKey,
  keyExpireDurationInHours,
  serverTags,
  serverLocation,
  tokenBasedOnboardingPhase2Flag,
  secureGatewayFlag,
  applianceId,
) {
  // expiresIn: expiration of activation key in hours
  const payload = {
    expirationInHours: keyExpireDurationInHours,
    subscriptionKey,
  };

  if (tokenBasedOnboardingPhase2Flag) {
    payload.location = serverLocation;
    payload.tags = serverTags;
  }
  if (secureGatewayFlag && applianceId && applianceId !== DIRECT_CONNECT) {
    payload.gatewayApplianceId = `gateway+${applianceId}`;
  }

  return axios.post(URLS.SERVER_ACTIVATION_TOKEN_URL, payload);
}
export async function getPinBasedDeviceActivationKey(
  subscriptionKey,
  connectionType,
  keyExpireDurationInHours,
  applianceId,
  locationId,
  sdc,
  tags,
  autoDiscoverSubscription,
) {
  // expiresIn: expiration of activation key in hours
  const payload = {
    expirationInHours: keyExpireDurationInHours,
    targetDevice: TARGET_DEVICE_ILO,
  };

  if (subscriptionKey) {
    payload.subscriptionKey = subscriptionKey;
  }
  // check for connection type before adding applianceUri
  if (connectionType === SECURE_GATEWAY && applianceId) {
    payload.applianceUri = `${URLS.GATEWAY_APPLIANCE_URL}+${applianceId}`;
  }

  payload.locationId = locationId;
  payload.serviceDeliveryContact = sdc;
  payload.tags = tags;

  if (autoDiscoverSubscription) {
    payload.autoDiscoverSubscription = true;
  }

  return axios.post(URLS.ACTIVATION_KEYS_URL, payload);
}

export async function getGatewayActivationKey(keyExpireDurationInHours) {
  const payload = {};

  payload.expirationInHours = keyExpireDurationInHours;
  payload.targetDevice = TARGET_DEVICE_SECURE_GATEWAY;

  return axios.post(URLS.ACTIVATION_KEYS_URL, payload);
}

export async function onboardServers(formValues) {
  const payload = {
    secureGateway: {
      fqdn: formValues?.secureGatewayName,
      applianceID: formValues?.secureGatewayId,
    },
    dns: formValues?.isDnsNtpConsent ? formValues?.dns : '',
    ntp: formValues?.isDnsNtpConsent ? formValues?.ntp : '',
    hideUnselectedServers: formValues?.hideUnselectedServers ? TRUE : FALSE,
    subscriptions: [],
    location: formValues?.location
      ? { id: formValues.location?.id, name: formValues.location?.name }
      : null,
    serviceDeliveryContact: formValues?.serviceDeliveryContact?.email
      ? formValues?.serviceDeliveryContact?.email
      : '',
    tags: {},
  };

  payload.ilo =
    formValues?.selectedServersInAutoDiscover?.map(serverDetails => ({
      ...serverDetails,
      iloUpdate: serverDetails?.iloUpdate === TRUE,
    })) || [];

  if (formValues?.selectedSubscriptionKeys?.length) {
    payload.subscriptions =
      formValues?.selectedSubscriptionKeys?.map(item => ({
        subscriptionKey: item.subKey,
        availableSeats: item.remainSeats,
      })) || [];
  }

  if (formValues?.autoAddServerTag?.length) {
    formValues?.autoAddServerTag?.forEach(tag => {
      payload.tags[tag.name] = tag.value;
    });
  }

  if (
    formValues?.subscriptionKeyOption === SELECT_SUBSCRIPTION_KEY_AUTO_DISCOVER
  ) {
    payload.autoDiscoverSubscription = true;
  }

  return axios.post(URLS.OPS_MGMT_ONBOARD_SERVERS_URL, payload);
}

export async function addServer({
  host,
  username,
  password,
  certificateFingerprint,
  applianceId,
}) {
  const payload = {
    hardware: {
      bmc: {
        userDefined: {
          host,
          username,
          password,
          certificateFingerprint,
        },
      },
    },
    appliance: {
      applianceId,
    },
  };
  const response = await axios.post(
    `${URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL}`,
    payload,
  );

  return response.data;
}

export async function editServer({
  id,
  host,
  username,
  password,
  certificateFingerprint,
  applianceId,
}) {
  // If username or password is empty, set to undefined
  // to avoid sending empty strings in the request
  username = username || undefined;
  password = password || undefined;

  const payload = {
    hardware: {
      bmc: {
        userDefined: {
          host,
          username,
          password,
          certificateFingerprint,
        },
      },
    },
    appliance: {
      applianceId,
    },
  };

  const response = await axios.patch(
    `${URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL}/${id}`,
    payload,
    {
      headers: { 'content-type': 'application/merge-patch+json' },
    },
  );

  return response.data;
}

export async function updateServerCredentials(id, password) {
  const payload = {
    hardware: {
      bmc: {
        userDefined: {
          password: password || undefined,
        },
      },
    },
  };

  const response = await axios.patch(
    `${URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL}/${id}`,
    payload,
    {
      headers: { 'content-type': 'application/merge-patch+json' },
    },
  );

  return response.data;
}

export const removeServer = async id =>
  axios.delete(`${URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL}/${id}`);

export const deleteServers = serverIds =>
  axios.post(URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL_BULK_DELETE, {
    serverIds,
  });

export const reconnectServer = serverId => {
  const requestBody = {
    resourceUri: `/compute-ops-mgmt/v1beta2/servers/${serverId}`,
    jobTemplateUri: `${SERVER_RECONNECT_TEMPLATE_URI}`,
  };
  const promise = createJob({
    ...requestBody,
  });

  return promise;
};

export async function configureVcenter(reqBody) {
  return axios.post(URLS.OPS_MGMT_EXTERNAL_SERVICE_URL, reqBody);
}

export async function editVcenterConfig(id, reqBody) {
  return axios.patch(`${URLS.OPS_MGMT_EXTERNAL_SERVICE_URL}/${id}`, reqBody, {
    headers: { 'content-type': 'application/merge-patch+json' },
  });
}

export async function getActivationKeys(filterString) {
  const params = {};
  filterString && (params.filter = filterString);

  return axios.get(URLS.ACTIVATION_KEYS_URL, { params });
}

export async function getActivationKeyData(id) {
  return axios.get(`${URLS.ACTIVATION_KEYS_DATA_URL}/${id}`);
}

export async function deleteActivationKey(id) {
  return axios.delete(`${URLS.ACTIVATION_KEYS_URL}/${id}`);
}

export async function getOvServerTemplates(offset, count, filterString) {
  const params = {};
  offset && (params.offset = offset);
  count && (params.limit = count);
  filterString && (params.filter = filterString);

  return axios.get(`${URLS.OPS_MGMT_OV_SERVER_TEMPLATES_URL}`, {
    params,
  });
}

export async function getAllOvServerTemplates(applianceId) {
  const localFilterString = `eq(applianceId,'${applianceId}')`;

  return getAllPages(
    (offset, limit) => getOvServerTemplates(offset, limit, localFilterString),
    COUNTS.MAX_IRIS_INVENTORY_COUNT,
  );
}

export const getOvServerTemplatesByUri = async serverTemplateUri =>
  axios.get(
    // eslint-disable-next-line max-len
    `${URLS.OPS_MGMT_OV_SERVER_TEMPLATES_URL}?filter=and(eq(uri,'${serverTemplateUri}'))`,
  );
export function clearUtilizationAlert(alert) {
  const payload = {
    alerts: [
      {
        messageId: alert.messageId,
        originOfCondition: alert.originOfCondition,
      },
    ],
  };
  return axios.post(
    `${URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL}/${alert.serverId}/clear-alert`,
    payload,
  );
}

export function clearServerAlert(alert) {
  const payload = {
    cleared: true,
  };
  return axios.patch(`${URLS.ALERTS_URL_V1BETA1}/${alert.id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  });
}

export const getActiveUtilizationAlertsByDeviceId = async deviceId =>
  axios.get(
    // eslint-disable-next-line max-len
    `${URLS.SERVERS_URL}/${deviceId}/alerts?filter=category eq 'UtilizationThreshold'&filter=clearedAt eq null`,
  );

export const getActiveServerAlertsByDeviceId = async (
  deviceId,
  swAlertCategories,
) => {
  let categories = '';
  swAlertCategories.forEach(category => {
    categories += `'${category}',`;
  });
  return axios.get(
    // eslint-disable-next-line max-len
    `${URLS.SERVERS_URL}/${deviceId}/alerts?filter=in(category, ${categories})&filter=clearedAt eq null`,
  );
};

export const getOvServerTemplatesByUris = async serverTemplateUris => {
  let uris = '';
  serverTemplateUris.forEach(uri => {
    uris += `'${uri}',`;
  });
  uris = uris.slice(0, -1);
  return axios.get(
    // eslint-disable-next-line max-len
    `${URLS.OPS_MGMT_OV_SERVER_TEMPLATES_URL}?filter=in(uri, ${uris})`,
  );
};

export const updateOneViewServerTemplatesJob = (settingsId, templatesList) => {
  const url = URLS.OPS_MGMT_SETTING_V1_URI_PREFIX;
  const settingsUri = `${url}/${settingsId}`;
  const requestBody = {
    jobTemplateUri: `${OV_SERVER_TEMPLATE_UPDATE_URI}`,
    resourceUri: settingsUri,
    data: {
      templates_data: templatesList,
    },
  };
  const promise = createJobV1B2({
    ...requestBody,
  });
  return promise;
};

export const getExistingOneViewServerTemplateUpdateJob = async settingsId => {
  const ovServerTemplateUpdateJobTemplateId = getIdFromUri(
    OV_SERVER_TEMPLATE_UPDATE_URI,
  );
  const url = URLS.OPS_MGMT_SETTING_V1_URI_PREFIX;
  const settingsUri = `${url}/${settingsId}`;

  const filter =
    // eslint-disable-next-line max-len
    `(associated_resource_uri='${settingsUri}' and job_template_id='${ovServerTemplateUpdateJobTemplateId}' and (state='${JOB_STATE.PENDING.toLowerCase()}' or state='${JOB_STATE.RUNNING.toLowerCase()}'))`;
  const promise = getJobsV1B2(filter);
  return promise;
};

export async function getServerLocations() {
  return axios.get(URLS.LOCATIONS_URL);
}

export const getLocations = async (offset, limit, sort, filters) => {
  const params = {};
  if (limit) params.limit = limit;
  if (filters) params.filter = `(${filters.join(') and (')})`;
  if (offset) params.offset = offset;
  return axios.get(URLS.LOCATIONS_URL, {
    params,
  });
};

export const getAllLocations = async (sort, filterQueries) => {
  const locations = [];
  let fetches = [];
  let total = 0;
  let count = 0;
  const firstFetch = await getLocations(
    1,
    COUNTS.MAX_DOORWAY_COUNT,
    sort,
    filterQueries,
  ).catch(e => {
    throw e;
  });

  if (firstFetch?.data?.total) {
    total = firstFetch.data.total;
  }
  if (firstFetch?.data?.items?.length) {
    count = firstFetch.data.items.length;
  }

  if (total > count) {
    const promiseArray = [];
    const numFetches = Math.ceil(total / COUNTS.MAX_DOORWAY_COUNT);
    for (let i = 1; i < numFetches; i++) {
      promiseArray.push(
        getLocations(
          i * COUNTS.MAX_DOORWAY_COUNT,
          COUNTS.MAX_DOORWAY_COUNT,
          sort,
          filterQueries,
        ),
      );
    }

    fetches = await Promise.all(
      promiseArray.map(p =>
        p.catch(e => {
          throw e;
        }),
      ),
    ).then(values => values);
  }

  fetches.splice(0, 0, firstFetch);

  fetches.forEach(fetch => {
    if (fetch?.data?.items) {
      fetch.data.items.forEach(setting => {
        locations.push(setting);
      });
    }
  });

  return locations;
};

export const getUsers = async () => axios.get(URLS.ALL_USERS_URL);

export const getExistingServerVolumeDetailsJob = async serverId => {
  const encodedServerId = serverId.replace(/\+/g, '%2B');
  const serverVolumeDetailsJobTemplateId = getIdFromUri(
    SERVER_VOLUME_DETAILS_TEMPALTE_URI,
  );
  // eslint-disable-next-line max-len
  const filter = `(associated_resource_uri='/compute-ops-mgmt/v1beta2/servers/${encodedServerId}' and job_template_id='${serverVolumeDetailsJobTemplateId}' and (state='${JOB_STATE.PENDING.toLowerCase()}' or state='${JOB_STATE.RUNNING.toLowerCase()}'))`;
  const promise = getJobsV1B2(filter);
  return promise;
};

export const createViewServerVolumeDetailsJob = serverId => {
  const requestBody = {
    resourceUri: `/compute-ops-mgmt/v1beta2/servers/${serverId}`,
    jobTemplateUri: `${SERVER_VOLUME_DETAILS_TEMPALTE_URI}`,
  };
  const promise = createJobV1B2({
    ...requestBody,
  });

  return promise;
};

export const getServerVolumeDetails = serverId => {
  const url = `${URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL}/${serverId}/external-storage-details`;
  return axios.get(url);
};
export const getOVLocations = (limit, filters, next) => {
  const params = {};
  if (limit) params.limit = limit;
  if (filters) params.filter = `(${filters.join(') and (')})`;
  if (next) params.next = next;
  return axios.get(URLS.LOCATIONS_URL, {
    params,
  });
};

export const deleteOVLocations = (serverStr, locationId) => {
  const headers = {
    headers: { 'Content-Type': 'application/json' },
  };
  const url = `${URLS.SERVER_LOCATIONS_URL}/${locationId}/servers?id=${serverStr}`;
  return axios.delete(url, {}, headers);
};

export const setOVLocations = (servers, location) => {
  const payload = { servers };
  const headers = {
    headers: { 'Content-Type': 'application/json' },
  };
  const url = `${URLS.SERVER_LOCATIONS_URL}/${location.id}/servers`;
  return axios.post(url, payload, headers);
};

export function getCustomers(
  mspAcid,
  offset,
  count,
  sort,
  searchString,
  filterQueries,
) {
  const params = {};
  const filters = filterQueries || [];

  offset && (params.offset = offset);
  count && (params.limit = count);
  sort && (params.sort = sort);
  searchString &&
    (params.search = `${searchString
      .trim()
      .replaceAll('\\', '\\\\')
      .replaceAll("'", "\\'")}`);

  if (filters && filters.length > 0) {
    if (filters instanceof Array) {
      params.filter = `(${filters.join(') and (')})`;
    } else {
      params.filter = filters;
    }
  }

  return axios.get(`${URLS.CUSTOMERS_URL}/${mspAcid}/tenants`, {
    params,
  });
}

export function getCustomerCounts(mspAcid) {
  return axios.get(`${URLS.CUSTOMERS_URL}/${mspAcid}/tenants/counts`);
}

export function getCustomersData(
  format,
  mspAcid,
  sort,
  searchString,
  filterQueries,
) {
  const params = {};
  const filters = filterQueries || [];

  format && (params.format = format);
  sort && (params.sort = sort);
  searchString &&
    (params.search = `${searchString
      .trim()
      .replaceAll('\\', '\\\\')
      .replaceAll("'", "\\'")}`);

  if (filters && filters.length > 0) {
    if (filters instanceof Array) {
      params.filter = `(${filters.join(') and (')})`;
    } else {
      params.filter = filters;
    }
  }

  return axios.get(`${URLS.CUSTOMERS_URL}/${mspAcid}/data`, {
    params,
  });
}

export function createCopyServerTemplatesJob(groupId, selectedDevices) {
  const url = URLS.OPS_MGMT_GROUPS_V1_URI_PREFIX;

  const groupUri = `${url}/${groupId}`;

  const requestBody = {
    resourceUri: groupUri,
    jobTemplateUri: COPY_OV_SERVER_TEMPLATE_URI,
    data: {
      targetApplianceIds: selectedDevices.map(dev => dev.deviceId),
    },
  };

  const promise = createJobV1B2({
    ...requestBody,
  });
  return promise;
}

export function createApplyApplianceSettingsJob(
  groupId,
  selectedDevices,
  applianceUriDiscrepancyFlag,
) {
  const url = URLS.OPS_MGMT_GROUPS_V1_URI_PREFIX;

  const groupUri = `${url}/${groupId}`;

  const requestBody = {
    resourceUri: groupUri,
    jobTemplateUri: APPLY_OV_APPLIANCE_SETTINGS_TEMPLATE_URI,
    data: {
      targetApplianceIds: selectedDevices.map(dev =>
        applianceUriDiscrepancyFlag ? dev.id : dev.deviceId,
      ),
    },
  };
  const promise = createJobV1B2({
    ...requestBody,
  });
  return promise;
}

export function createRefreshApplianceSettingsJob(
  appliance,
  uriDiscrepancyFlag,
) {
  const requestBody = {
    resourceUri: uriDiscrepancyFlag
      ? appliance?.resourceUri
      : `${URLS.OPS_MGMT_OV_APPLIANCES_URI}/${appliance?.deviceId}`,
    jobTemplateUri: REFRESH_APPLIANCE_SETTINGS_TEMPLATE_URI,
  };
  const promise = createJobV1B2({
    ...requestBody,
  });
  return promise;
}

export function getLastAHSUploadDetails() {
  const params = {};
  params.sort = 'parsingStatus/statusModifiedAt desc';
  params.limit = 1;
  return axios.get(URLS.AHS_FILES_URL, { params });
}

export function getLastSupportAHSUploadDetails() {
  const params = {};
  params.sort = 'parsingStatus/statusModifiedAt desc';
  params.limit = 1;
  return axios.get(URLS.SUPPORT_AHS_FILES_URL, { params });
}

export async function createFileResourceAsync(fileName, fileCategory) {
  const requestBody = {
    fileName,
    fileCategory,
  };
  return axios.post(URLS.FILE_UPLOAD_URL, requestBody);
}

export function createAHSFileResource(fileName) {
  const requestBody = {
    fileName,
  };
  return axios.post(URLS.AHS_FILES_URL, requestBody);
}

export async function submitSupportCase(formData) {
  return axios.post(URLS.SUPPORT_CASE_URL, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export async function submitManualSupportCase(formData) {
  return axios.post(URLS.MANUAL_SUPPORT_CASE_URL, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export async function uploadAnyFileToS3(preSignedUrl, fileObject) {
  const formData = new FormData();
  const { parameters } = preSignedUrl;
  parameters.forEach(item => {
    formData.append(item.name, item.value);
  });

  formData.append('file', fileObject);
  return axios.post(preSignedUrl.baseUrl, formData, {
    omitAuth: true,
    headers: {
      'content-type': 'multipart/form-data',
    },
  });
}

export function uploadFileToS3(preSignedUrl, fileObject) {
  const formData = new FormData();
  const { parameters } = preSignedUrl;
  parameters.forEach(item => {
    formData.append(item.name, item.value);
  });

  formData.append('file', fileObject);
  return axios.post(preSignedUrl.baseUrl, formData, {
    omitAuth: true,
    headers: {
      'content-type': 'multipart/form-data',
    },
  });
}

export function patchAHSFileResource(fileUUID, requestBody) {
  return axios.patch(`${URLS.AHS_FILES_URL}/${fileUUID}`, requestBody, {
    headers: { 'content-type': 'application/merge-patch+json' },
  });
}

export function initiateAHSAnalysis(fileUUID, components) {
  const requestBody = {
    components,
  };
  return axios.post(`${URLS.AHS_FILES_URL}/${fileUUID}/parse`, requestBody);
}

export function patchSupportAHSFileResource(fileUUID, requestBody) {
  return axios.patch(`${URLS.SUPPORT_AHS_FILES_URL}/${fileUUID}`, requestBody, {
    headers: { 'content-type': 'application/merge-patch+json' },
  });
}

export function createSupportAHSFileResource(fileName, caseId) {
  const requestBody = {
    fileName,
    caseId,
  };
  return axios.post(URLS.SUPPORT_AHS_FILES_URL, requestBody);
}

export function initiateSupportAHSAnalysis(fileUUID, components) {
  const requestBody = {
    components,
  };
  return axios.post(
    `${URLS.SUPPORT_AHS_FILES_URL}/${fileUUID}/parse`,
    requestBody,
  );
}

export function getServerDataFromAHS(filePath, id) {
  const params = {
    filename: filePath,
  };
  return axios.get(`${URLS.AHS_FILES_URL}/${id}/download`, { params });
}

export function getAHSServers(
  offset,
  count,
  sort,
  searchString,
  filterQueries,
) {
  const params = {};
  const filters = filterQueries || [];
  params.offset = offset ?? 0;
  count && (params.limit = count);
  sort && (params.sort = sort);

  searchString &&
    (params.search = `${searchString
      .trim()
      .replaceAll('\\', '\\\\')
      .replaceAll("'", "\\'")}`);

  filters.length > 0 && (params.filter = `(${filters.join(') and (')})`);

  return axios.get(URLS.AHS_SERVERS_URL, { params });
}

export function getAHSServerFiles(id) {
  return axios.get(`${URLS.AHS_SERVERS_URL}/${id}/ahs-files`, {
    params: {
      filter:
        "parsingStatus/status in ('ANALYSIS_SUCCESS','ANALYSIS_IN_PROGRESS')",
      sort: 'parsingStatus/statusModifiedAt desc',
    },
  });
}

export function getAHSServerActivities() {
  return axios.get(URLS.UI_DOORWAY_ACTIVITIES_URL, {
    params: {
      groups: COUNTS.NUM_ITEMS_IN_ACTIVITY_CARD,
      filter: `(source/type eq '${SUPPORT_AHS_REFRESH_TOPIC}')`,
    },
  });
}

export function getCertificateDataFromServer(applianceId) {
  const CERTIFICATE_API_URL =
    `${URLS.COM_API_ROOT_URL}` +
    `${URLS.GATEWAY_APPLIANCE_URL}+${applianceId}/certificate`;
  return axios.get(`${CERTIFICATE_API_URL}`);
}

export function getServerInsights() {
  return axios.get(URLS.SERVER_INSIGHTS_URL);
}

export function getServerInsightsByType(type) {
  return axios.get(URLS.SERVER_INSIGHTS_V2_URL, {
    params: { insightTypes: type },
  });
}

export function getInsightsCount() {
  const url = `${URLS.SERVERS_COUNTS_URL}?fields[]=insights_ `;
  return axios.get(url);
}

export function cancelPendingJob(id) {
  return axios.delete(`${URLS.OPS_MGMT_JOBS_V1B3_URL}/${id}`);
}

export function getServerSSO(id, ssoUri) {
  return axios.get(`${URLS.COM_API_ROOT_URL}${ssoUri}`);
}

export function exportSustainabilityReport(
  startDate,
  endDate,
  projectionDays,
  valueType,
  resourceUri = ALL_SERVERS_FILTER_MGMT_URL,
  sampleSize = null,
  filterTags = null,
) {
  const params = {};
  if (startDate) params['start-date'] = startDate;
  if (projectionDays) params['projection-days'] = projectionDays;
  if (endDate) params['end-date'] = endDate;
  if (filterTags) {
    params['filter-tags'] = filterTags;
  }
  if (resourceUri) {
    params['resource-uri'] = resourceUri;
  }
  if (sampleSize) params['sample-size'] = sampleSize;

  const headers =
    valueType === 'CSV'
      ? { accept: 'text/csv, text/plain, */*' }
      : { accept: 'application/json, text/plain, */*' };

  return axios.get(`${URLS.ENERGY_DATA_EXPORT_URL}`, {
    params,
    headers,
  });
}

export function getServersFilterCount(filterId, offset, limit) {
  const params = {};
  params.offset = offset || 0;
  limit && (params.limit = limit);
  return axios.get(`${URLS.OPS_MGMT_SAVED_FILTERS_URL}/${filterId}/matches`, {
    params,
  });
}

export function getSustainabilityAnalysisTableData(
  startDate,
  projectionDays,
  excludedServers,
  aggregateBy,
  exportType,
  endDate,
  offset,
  count,
  sort,
  filter,
  resourceUri = ALL_SERVERS_FILTER_MGMT_URL,
  sampleSize = null,
  filterTags = null,
  aggregationTagNames = null,
) {
  const params = {};
  params.offset = offset || 0;
  count && (params.limit = count);
  sort && (params.sort = sort);
  if (startDate) params['start-date'] = startDate;
  if (excludedServers) params['excluded-servers'] = excludedServers;
  if (projectionDays) params['projection-days'] = projectionDays;
  if (aggregateBy) params['aggregate-by'] = aggregateBy;
  if (exportType) params['export-as'] = exportType;
  if (endDate) params['end-date'] = endDate;
  if (filter) params.filter = filter;
  if (filterTags) {
    params['filter-tags'] = filterTags;
  }
  if (resourceUri) {
    params['resource-uri'] = resourceUri;
  }
  if (sampleSize) params['sample-size'] = sampleSize;
  if (aggregationTagNames)
    params['aggregation-tag-names'] = aggregationTagNames;
  return axios.get(`${URLS.ENERGY_BY_ENTITY_URL}`, { params });
}

export function exportServerUtilizationReport(
  startDate,
  endDate,
  valueType,
  metricType,
  resourceUri = ALL_SERVERS_FILTER_MGMT_URL,
  filterTags = null,
  sampleSize,
) {
  const params = {};
  if (startDate) params['start-date'] = startDate;
  if (metricType) params['metric-type'] = metricType;
  if (endDate) params['end-date'] = endDate;
  if (filterTags) {
    params['filter-tags'] = filterTags;
  }
  if (resourceUri) {
    params['resource-uri'] = resourceUri;
  }
  if (sampleSize) params['sample-size'] = sampleSize;

  const headers =
    valueType === 'CSV'
      ? { accept: 'text/csv, text/plain, */*' }
      : { accept: 'application/json, text/plain, */*' };

  return axios.get(`${URLS.UTILIZATION_DATA_EXPORT_URL}`, {
    params,
    headers,
  });
}

export function getServerUtilizationTableData(
  startDate,
  projectionDays,
  excludedServers,
  exportType,
  endDate,
  metricType,
  offset,
  count,
  sort,
  resourceUri = ALL_SERVERS_FILTER_MGMT_URL,
  filterTags = null,
  sampleSize,
) {
  const params = {};
  params.offset = offset || 0;
  count && (params.limit = count);
  sort && (params.sort = sort);
  if (startDate) params['start-date'] = startDate;
  if (excludedServers) params['excluded-servers'] = excludedServers;
  if (projectionDays) params['projection-days'] = projectionDays;
  if (exportType) params['export-as'] = exportType;
  if (endDate) params['end-date'] = endDate;
  if (metricType) params['metric-type'] = metricType;
  if (filterTags) {
    params['filter-tags'] = filterTags;
  }
  if (resourceUri) {
    params['resource-uri'] = resourceUri;
  }
  if (sampleSize) params['sample-size'] = sampleSize;

  return axios.get(`${URLS.UTILIZATION_BY_ENTITY_URL}`, { params });
}

export async function getServersForEachAggregateBy(
  startDate,
  aggregateBy,
  endDate,
  filter,
  serverCount,
  forecastDays,
) {
  // TODO - validate that serverCount is not needed for iterating all of the pages?

  return getAllPages(
    (offset, limit) =>
      getSustainabilityAnalysisTableData(
        startDate,
        // endDate is not needed for projection, only for historical
        endDate == null ? forecastDays : null,
        null,
        aggregateBy,
        null,
        endDate,
        offset,
        limit,
        null,
        filter,
      ),
    COUNTS.MAX_GET_SERVERS_ANALYTICS_COUNT,
  );
}

export async function getApprovalRequests(
  start,
  count,
  sort,
  searchString,
  filterQueries,
) {
  const params = {};
  start && (params.offset = start);
  count && (params.limit = count);
  sort && (params.sort = sort);
  searchString &&
    (params.search = `${searchString
      .trim()
      .replaceAll('\\', '\\\\')
      .replaceAll("'", "\\'")}`);
  const filters = filterQueries || [];

  if (filters && filters.length > 0) {
    params.filter = `(${filters.join(') and (')})`;
  }
  return axios.get(`${URLS.APPROVAL_REQUESTS_URL}`, { params });
}

export function getApprovalRequestById(requestId) {
  return axios.get(
    `${URLS.APPROVAL_REQUESTS_URL}/${encodeURIComponent(requestId)}`,
  );
}

export function getApprovalRequestTotalCount() {
  const params = {
    offset: 0,
    limit: 1,
  };

  return axios.get(URLS.APPROVAL_REQUESTS_URL, {
    params,
  });
}

export async function updateRequestState(
  requestId,
  payload,
  approvalV1Beta2MigrationFlag,
) {
  const url = approvalV1Beta2MigrationFlag
    ? `${URLS.OPS_MGMT_APPROVAL_REQUESTS_V1B2_URL}/${requestId}/approve`
    : `${URLS.APPROVAL_REQUESTS_COMPUTE_OPS_URL}/${requestId}/approvals`;
  return axios.post(url, payload);
}

export async function cancelRequest(
  requestId,
  payload,
  approvalV1Beta2MigrationFlag,
) {
  const url = approvalV1Beta2MigrationFlag
    ? URLS.OPS_MGMT_APPROVAL_REQUESTS_V1B2_URL
    : URLS.APPROVAL_REQUESTS_COMPUTE_OPS_URL;
  return axios.patch(`${url}/${requestId}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  });
}

// Chat API functions
export async function postQuery(query, sessionId = null) {
  const timeZone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;
  return axios.post(
    URLS.CHAT_URL,
    { query, sessionId },
    {
      headers: timeZone
        ? {
            'Time-Zone': timeZone,
          }
        : {},
    },
  );
}

// Returns chat response asynchronously
export async function getChatAsync(chatId) {
  return axios.get(`${URLS.CHAT_URL}/${chatId}/response`, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function sendChatFeedback(query, rating, feedback, chatId) {
  const body = {
    question: query,
    rating,
    feedback,
  };

  return axios.patch(`${URLS.CHAT_URL}/${chatId}`, body, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function getApprovalPolicies(
  start,
  count,
  sort,
  searchString,
  filterQueries,
) {
  const params = {};

  start && (params.offset = start);
  count && (params.limit = count);
  sort && (params.sort = sort);
  searchString &&
    (params.search = `${searchString
      .trim()
      .replaceAll('\\', '\\\\')
      .replaceAll("'", "\\'")}`);
  const filters = filterQueries || [];
  if (filters && filters.length > 0) {
    params.filter = `(${filters.join(') and (')})`;
  }

  return axios.get(`${URLS.APPROVAL_POLICY_URL}`, { params });
}

export async function getAllApprovalPolicies(sort, filterQueries) {
  return getAllPages(
    (offset, limit) =>
      getApprovalPolicies(
        offset,
        limit,
        sort,
        null, // search
        filterQueries,
      ),
    COUNTS.MAX_DOORWAY_COUNT,
  );
}

export function getApprovalPolicyTotalCount() {
  const params = {
    offset: 0,
    limit: 1,
  };

  return axios.get(URLS.APPROVAL_POLICY_URL, {
    params,
  });
}

export async function getApprovalPolicyById(policyId) {
  return axios.get(`${URLS.APPROVAL_POLICY_URL}/${policyId}`);
}

export async function getApproverList(slugQueries) {
  const params = new URLSearchParams();
  if (slugQueries.length > 0) {
    slugQueries.forEach(slug => params.append('slugs', slug));
  }

  return axios.get(`${URLS.USERS_URL}`, { params });
}

export function getWorkspaceUsers() {
  return axios.get(URLS.WORKSPACE_USERS_URL);
}

export async function getApprovables() {
  return axios.get(`${URLS.APPROVABLES_URL}`);
}

export function createApprovalPolicy(payload, approvalV1Beta2MigrationFlag) {
  const url = approvalV1Beta2MigrationFlag
    ? URLS.OPS_MGMT_APPROVAL_POLICIES_V1B2_URL
    : URLS.OPS_MGMT_APPROVAL_POLICY_URL;
  return axios.post(url, payload);
}

// Get a page of groups using an approval policy
export async function getGroupsUsingPolicy(policyId, offset, limit) {
  let url = URLS.OPS_MGMT_APPROVAL_POLICIES_V1_URI_PREFIX;
  const params = {};
  const filters = [`approvalPolicy/id eq '${policyId}'`];

  if (filters && filters.length > 0) {
    if (filters instanceof Array) {
      params.filter = `(${filters.join(') and (')})`;
    } else {
      params.filter = filters;
    }
  }

  offset && (params.offset = offset);
  limit && (params.limit = limit);

  url = URLS.GROUPS_URL_V2;
  return axios.get(`${url}`, {
    params,
  });
}

export async function getAllGroupsUsingPolicy(policyId) {
  return getAllPages(
    (offset, limit) => getGroupsUsingPolicy(policyId, offset, limit),
    COUNTS.MAX_DOORWAY_COUNT,
  );
}

export function deleteApprovalPolicy(id, approvalV1Beta2MigrationFlag) {
  const url = approvalV1Beta2MigrationFlag
    ? URLS.OPS_MGMT_APPROVAL_POLICIES_V1B2_URL
    : URLS.OPS_MGMT_APPROVAL_POLICY_URL;

  return axios.delete(`${url}/${id}`);
}

export function updateApprovalPolicy(
  policyId,
  payload,
  approvalV1Beta2MigrationFlag,
) {
  const url = approvalV1Beta2MigrationFlag
    ? URLS.OPS_MGMT_APPROVAL_POLICIES_V1B2_URL
    : URLS.OPS_MGMT_APPROVAL_POLICY_URL;

  return axios.patch(`${url}/${policyId}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  });
}

export function approvalPolicyCheck(payload) {
  const url = URLS.APPROVAL_POLICY_CHECK_URL;

  return axios.post(`${url}`, payload, {
    headers: { 'Content-Type': 'application/json' },
  });
}

export const fetchApprovalRequiredDevices = async (
  devices,
  approvableMapId,
  setShowToast,
  onClose,
  setToastError,
) => {
  const payload = [];

  devices.forEach(device => {
    const devicePayload = {
      approvableMapId,
      resourceId: device.id,
      resourceType: 'compute-ops-mgmt/server',
    };

    payload.push(devicePayload);
  });

  let devicesApprovalReqInfo = [];

  try {
    const res = await approvalPolicyCheck(payload);
    if (res?.data) {
      devicesApprovalReqInfo = res.data;
    }
  } catch (e) {
    if (setToastError && setShowToast && onClose) {
      setToastError(e);
      setShowToast(true);
      onClose();
    }
  }

  return devicesApprovalReqInfo;
};

export function getSecureGatewayDownloadImage(downloadType) {
  const params = {};
  if (downloadType) {
    params['file-type'] = downloadType;
  }
  return axios.get(URLS.SECURE_GATEWAY_DOWNLOAD_URL, { params });
}

export function analyzeBaselineExclusions(settingId, payload) {
  return axios.post(
    `${URLS.OPS_MGMT_SETTINGS_URL}/${settingId}/analyze-baseline-exclusions`,
    payload,
    {
      headers: { 'Content-Type': 'application/json' },
    },
  );
}

export function addFirmwareBaseline({ file, onUploadProgress }) {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${URLS.FIRMWARE_BUNDLES_URL_V1}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  });
}

export function deleteFirmwareBaseline(id) {
  return axios.delete(`${URLS.FIRMWARE_BUNDLES_URL_V1}/${id}`);
}

export function getApiResponse(url) {
  return axios.get(`${URLS.COM_API_ROOT_URL}${url}`);
}

export function createMetricsCollectionRequest() {
  return axios.post(URLS.METRICS_COLLECTION_REQUESTS_URL, {
    requestType: 'SUSTAINABILITY_METRICS_COLLECTION',
  });
}

export function getAsyncOperation(id) {
  return axios.get(`${URLS.ASYNC_OPERATIONS_URL}/${id}`);
}

export function cancelAsyncOperation(id) {
  return axios.post(`${URLS.ASYNC_OPERATIONS_URL}/${id}/cancel`, {});
}

export function getRunningMetricsCollections() {
  return axios.get(URLS.ASYNC_OPERATIONS_URL, {
    params: {
      next: ASYNC_OPERATIONS_INITIAL_NEXT,
      filter:
        // eslint-disable-next-line max-len
        "state in ('INITIALIZED', 'RUNNING') and operationType eq 'SUSTAINABILITY_METRICS_COLLECTION'",
    },
  });
}

export function fetchRegionServersWithLocations(region, useOverviewApi = true) {
  const apiPath = window.indigo?.REACT_APP_COM_API_PATH || '';
  const regionApiRoot = `${region.url}${apiPath}`;
  const baseUrl = useOverviewApi ? URLS.SERVERS_OVERVIEW_URL : URLS.SERVERS_URL;
  const url = baseUrl.replace(URLS.COM_API_ROOT_URL, regionApiRoot);
  const filter = [`not locationInfo_/name in (${null})`];

  return getAllPages((offset, limit) => {
    const params = getParamsForServers(offset, limit, null, null, filter);
    return axios.get(url, { params });
  }, COUNTS.MAX_DOORWAY_COUNT);
}
